import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell,
} from "recharts";
import {
  ArrowLeft, Users, MousePointerClick, Heart, DollarSign,
  TrendingUp, BookOpen, Globe, Gift, Mail, Send, Clock, AlertTriangle, Eye,
  Database, RefreshCw, Loader2, MapPin, Plus, Copy, Trash2, CheckCircle2,
  RotateCw, AlertCircle, Ban, Filter as FilterIcon, Wrench,
  ImageOff, MapPinOff, MapPinned, ShieldAlert, Pencil, ExternalLink,
  Hotel, PlayCircle, ListChecks, CalendarClock, Info,
} from "lucide-react";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { _resetBookingPreferenceCache } from "@/lib/bookingPreference";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import ReviewerLockdown from "@/components/ReviewerLockdown";
import UsersRolesPanel from "@/components/UsersRolesPanel";
import UserBackpackPanel from "@/components/UserBackpackPanel";
import HotelbedsAdminPanel from "@/components/HotelbedsAdminPanel";
import HotelbedsContentSyncPanel from "@/components/HotelbedsContentSyncPanel";
// Phase 5 (2026-07-28): GooglePlacesUsagePanel retired with Google.
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

/* ---------- Legacy catalog visibility ----------
 *
 * The UI panel that used to expose these controls was removed after
 * the 2026-07 Google Places cutover — the underlying endpoints
 * (`/api/admin/catalog/hide-all`, `/restore-all`, `/visibility-status`)
 * are still wired in `routers/admin_catalog_visibility.py` and can be
 * hit via curl in an emergency. The `CatalogVisibilityPanel` component
 * definition below is retained as dead code purely as a breadcrumb —
 * search-ability from a future PR that might want to bring the UI
 * back. Not mounted anywhere.
 */
function CatalogVisibilityPanel() {
  const [stats, setStats] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [confirmingHide, setConfirmingHide] = React.useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/catalog/visibility-status");
      setStats(data);
    } catch (e) {
      // Non-fatal — the panel just won't show numbers.
    }
  };
  useEffect(() => { refresh(); }, []);

  const hideAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalog/hide-all");
      toast.success(`Hidden ${data.modified} cards (${data.total_hidden} total).`);
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Hide-all failed");
    } finally {
      setBusy(false);
      setConfirmingHide(false);
    }
  };

  const restoreAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalog/restore-all");
      toast.success(`Restored ${data.modified} cards.`);
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore-all failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="admin-catalog-visibility-panel"
      className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-serif-it italic text-tb-navy text-xl md:text-2xl">
            Legacy catalog control
          </h2>
          <p className="font-hand text-sm text-tb-navy/60 mt-1">
            Bulk-hide or restore every card in <code>travelbio_destinations</code>.
            Nothing is deleted &mdash; hidden rows are just filtered out of Explore /
            grid / mobile. Ready for the Google Places PoC cutover; do not activate
            yet.
          </p>
        </div>
        {stats && (
          <div className="text-right font-hand text-xs text-tb-navy/65">
            <div data-testid="admin-catalog-total">{stats.total} total</div>
            <div data-testid="admin-catalog-hidden" className="text-tb-coral">{stats.hidden} hidden</div>
            <div data-testid="admin-catalog-visible" className="text-tb-blue">{stats.visible} visible</div>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="admin-catalog-hide-btn"
          disabled={busy}
          onClick={() => setConfirmingHide(true)}
          className="inline-flex items-center gap-1.5 rounded-full bg-tb-coral text-white border-2 border-tb-coral px-4 h-10 font-hand text-sm tb-tap hover:brightness-105 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
          Hide all catalog cards
        </button>
        <button
          type="button"
          data-testid="admin-catalog-restore-btn"
          disabled={busy}
          onClick={restoreAll}
          className="inline-flex items-center gap-1.5 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold px-4 h-10 font-hand text-sm tb-tap hover:brightness-110 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
          Restore all catalog cards
        </button>
      </div>

      <AlertDialog open={confirmingHide} onOpenChange={setConfirmingHide}>
        <AlertDialogContent data-testid="admin-catalog-hide-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Hide every catalog card?</AlertDialogTitle>
            <AlertDialogDescription>
              Explore, grid, and mobile will show zero TravelBio-catalogue cards
              until you click <b>Restore all catalog cards</b>. Nothing is deleted &mdash;
              the rows stay in Mongo with <code>hidden=true</code>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-catalog-hide-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-catalog-hide-confirm-btn" onClick={hideAll}>
              Yes, hide them all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/* ---------- KPI Card ---------- */
function Kpi({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div data-testid={testId}
      className="rounded-2xl border-2 p-4 bg-tb-cream"
      style={{ borderColor: color }}>
      <div className="flex items-center justify-between">
        <span className="font-hand text-tb-navy/70 italic">{label}</span>
        <div className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: color, color: "#fff" }}>
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
      </div>
      <div className="font-serif text-3xl text-tb-navy mt-1 leading-tight">{value}</div>
      {sub && <div className="font-hand text-tb-navy/60 text-sm mt-0.5">{sub}</div>}
    </div>
  );
}

/* ---------- Page ---------- */
export default function AdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats]   = useState(null);
  const [tops, setTops]     = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [posts, setPosts]   = useState(null);
  const [clicks, setClicks] = useState(null);
  const [digest, setDigest] = useState(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) { nav("/auth"); return; }
    if (!user) return;
    if (user.role !== "admin" && user.role !== "reviewer") { nav("/explore"); return; }
    (async () => {
      try {
        const [s, t, f, p, c, d] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/top-destinations"),
          api.get("/admin/funnel"),
          api.get("/admin/top-posts"),
          api.get("/admin/recent-clicks"),
          api.get("/admin/digest/status"),
        ]);
        setStats(s.data); setTops(t.data); setFunnel(f.data);
        setPosts(p.data); setClicks(c.data); setDigest(d.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, [user, nav]);

  const sendDigestNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      const ds = await api.get("/admin/digest/status");
      setDigest(ds.data);
      if (data.skipped) {
        toast.error(data.reason || "Digest skipped");
      } else {
        toast.success(`Digest sent to ${data.sent} admin${data.sent === 1 ? "" : "s"} ✨`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const openPreview = () => {
    window.open(`${API_BASE}/admin/digest/preview`, "_blank", "noopener,noreferrer");
  };

  const fmt = (n) => n?.toLocaleString() ?? "0";

  const topDests = useMemo(() => (tops?.destinations || []).slice(0, 6), [tops]);
  const funnelData = useMemo(() => funnel?.stages || [], [funnel]);

  if (loading || !stats) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-tb-navy-deep">
        <span className="font-hand text-tb-gold-bright text-2xl">~ loading analytics ~</span>
      </div>
    );
  }

  return (
    <div
      data-testid={ADMIN.page}
      className={
        "min-h-screen w-full bg-tb-navy-deep " +
        (user?.role === "reviewer" ? "reviewer-locked" : "")
      }
    >
      <ReviewerLockdown />
      {/* Header bar */}
      <header className="bg-tb-navy text-tb-gold border-b-2 border-tb-gold/70 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            data-testid={ADMIN.backBtn}
            data-reviewer-safe="true"
            onClick={() => nav("/explore")}
            className="p-2 rounded-full border border-tb-gold/60 hover:bg-tb-gold/10 tb-tap"
            aria-label="Back to app"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="font-script text-4xl leading-none text-tb-gold-bright">
              Owner Compass
            </h1>
            <p className="font-hand text-tb-gold/80 text-sm italic">
              TravelBio · operator analytics
            </p>
          </div>
        </div>
        <span className="font-hand text-tb-gold/70 text-sm hidden sm:block">
          signed in as <b className="text-tb-gold-bright">{user?.email}</b>
        </span>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-7 space-y-7 bg-white min-h-[calc(100vh-72px)]">
        {/* Admin tools navigation */}
        <section className="flex flex-wrap gap-3">
          <button
            data-testid="admin-tile-destinations"
            data-reviewer-safe="true"
            onClick={() => nav("/admin/destinations")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap hover:bg-tb-navy/90"
          >
            <MapPin className="w-4 h-4" /> Destinations
          </button>
          <button
            data-testid="admin-tile-google-places"
            onClick={() => nav("/admin/google-places")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tb-cream-2 text-tb-navy border-2 border-tb-cream-3 font-hand text-sm tb-tap hover:border-tb-navy/40"
          >
            <MapPin className="w-4 h-4" /> Google Places (PoC)
          </button>
        </section>


        {/* Phase 5 (2026-07-28): Google Places usage telemetry
            retired. Hotelbeds observability lives under
            `HotelbedsAdminPanel` below. */}

        {/* Owner Backpack Lists — personal + business shopping lists +
            end-to-end smoke-test surface for the Amazon Associates tag. */}
        <UserBackpackPanel />
        {/* Hotelbeds admin panels — Batch 1 (2026-08-07) gated to
            cert_reviewer only. Non-cert admins get 403 from every
            HB endpoint, so hide the panels entirely for them to keep
            the console clean. */}
        {(user?.roles || []).includes("cert_reviewer") && (
          <>
            <HotelbedsAdminPanel />
            <HotelbedsContentSyncPanel />
          </>
        )}
        {/* KPI Row */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi testId={ADMIN.kpiUsers} icon={Users} label="travelers"
            value={fmt(stats.users)} color="#7cc4d6"
            sub={`${stats.chapters} chapters · ${stats.posts} posts`} />
          <Kpi testId={ADMIN.kpiClicks} icon={MousePointerClick} label="affiliate clicks"
            value={fmt(stats.clicks)} color="#d65d4f"
            sub={`${stats.saves} saves before click`} />
          <Kpi testId={ADMIN.kpiRevenue} icon={DollarSign} label="projected commission"
            value={`$${fmt(stats.projected_commission_usd)}`} color="#e6b84a"
            sub={`@ $40 / click`} />
          <Kpi testId={ADMIN.kpiSaves} icon={Heart} label="bookmarks"
            value={fmt(stats.saves)} color="#a695c7"
            sub={`vs ${stats.skips} skips`} />
        </section>

        {/* Conversion funnel */}
        <section data-testid={ADMIN.funnelChart}
          className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-5">
          <div className="flex items-end justify-between mb-3">
            <div>
              <h2 className="font-serif-it text-tb-navy text-2xl">Conversion funnel</h2>
              <p className="font-hand text-tb-navy/60 text-sm italic">
                swipe → save → click — the path to a paid booking
              </p>
            </div>
            <div className="text-right font-hand text-tb-navy/70 text-sm">
              <div>save rate <b className="text-tb-coral">{funnel?.save_rate ?? 0}%</b></div>
              <div>click rate <b className="text-tb-coral">{funnel?.click_rate ?? 0}%</b></div>
            </div>
          </div>
          <div className="flex items-stretch gap-3">
            {funnelData.map((stage, i) => {
              const max = Math.max(...funnelData.map((s) => s.value), 1);
              const pct = Math.max(8, (stage.value / max) * 100);
              const colors = ["#7cc4d6", "#a695c7", "#d65d4f"];
              return (
                <motion.div key={stage.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="flex-1 flex flex-col items-center">
                  <div className="font-hand text-tb-navy/70 text-sm mb-1">{stage.name}</div>
                  <div className="w-full rounded-xl flex flex-col items-center justify-end overflow-hidden text-white"
                    style={{
                      background: colors[i],
                      height: 130, width: `${100 - i * 14}%`,
                    }}>
                    <div className="pt-2 text-3xl font-serif">{fmt(stage.value)}</div>
                    <div className="pb-3 font-hand text-sm opacity-90">
                      {i === 0 ? "swipes" : `${Math.round(pct)}% of top`}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Top destinations chart */}
        <section data-testid={ADMIN.topDestinationsTable}
          className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif-it text-tb-navy text-2xl">Top destinations</h2>
            <span className="font-hand text-tb-navy/60 text-sm italic">
              by affiliate click volume
            </span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topDests} barGap={6}>
                <XAxis dataKey="name" tick={{ fontFamily: "Outfit", fontSize: 11, fill: "#1a3a5f" }} />
                <YAxis allowDecimals={false}
                  tick={{ fontFamily: "Outfit", fontSize: 11, fill: "#1a3a5f" }} />
                <Tooltip contentStyle={{
                  background: "#fdf6e7", border: "2px solid #e6b84a",
                  borderRadius: 10, fontFamily: "Outfit",
                }} />
                <Bar dataKey="saves" name="Saves" fill="#7cc4d6" radius={[6, 6, 0, 0]} />
                <Bar dataKey="clicks" name="Clicks" fill="#d65d4f" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full font-ui text-sm">
              <thead className="text-tb-navy/60 font-hand text-base">
                <tr className="border-b border-tb-cream-3">
                  <th className="text-left py-2">destination</th>
                  <th className="text-right">saves</th>
                  <th className="text-right">clicks</th>
                  <th className="text-right">ctr</th>
                  <th className="text-right">est. revenue</th>
                </tr>
              </thead>
              <tbody>
                {topDests.map((d) => (
                  <tr key={d.id} className="border-b border-tb-cream-3/60">
                    <td className="py-2 text-tb-navy">
                      <span className="font-hand text-tb-navy/60 mr-1.5">{d.code}</span>
                      <span className="font-serif-it text-base">{d.name}</span>
                      <span className="font-hand text-tb-navy/50 text-xs"> · {d.country}</span>
                    </td>
                    <td className="text-right text-tb-blue font-semibold">{d.saves}</td>
                    <td className="text-right text-tb-coral font-semibold">{d.clicks}</td>
                    <td className="text-right text-tb-navy/70">{d.ctr}%</td>
                    <td className="text-right font-semibold text-tb-navy">${d.projected_usd}</td>
                  </tr>
                ))}
                {topDests.length === 0 && (
                  <tr><td colSpan="5" className="py-6 text-center font-hand text-tb-navy/50">
                    no clicks yet — share TravelBio with someone!
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="grid lg:grid-cols-2 gap-7">
          {/* Top Lounge posts */}
          <section data-testid={ADMIN.topPostsList}
            className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif-it text-tb-navy text-xl">Top Lounge stories</h2>
              <BookOpen className="w-4 h-4 text-tb-navy/60" />
            </div>
            <ul className="space-y-2.5">
              {(posts?.posts || []).slice(0, 6).map((p, i) => (
                <li key={p.id} className="flex items-center gap-3">
                  <span className="font-serif-it text-tb-coral text-xl w-6 text-right">{i + 1}.</span>
                  <img src={p.author_avatar} alt="" className="w-9 h-9 rounded-full bg-tb-cream-2 object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-it text-tb-navy truncate">{p.author_name}</div>
                    <div className="font-hand text-tb-navy/60 text-xs truncate">{p.location}</div>
                  </div>
                  <div className="text-right font-hand text-tb-navy/70 text-sm">
                    <div><Heart className="w-3 h-3 inline -mt-0.5" /> {p.likes}</div>
                    <div className="text-tb-blue">{p.comments_count} comments</div>
                  </div>
                </li>
              ))}
              {(!posts?.posts || posts.posts.length === 0) && (
                <li className="font-hand text-tb-navy/50 text-center py-6">no stories yet</li>
              )}
            </ul>
          </section>

          {/* Recent affiliate clicks */}
          <section data-testid={ADMIN.recentClicks}
            className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif-it text-tb-navy text-xl">Recent affiliate clicks</h2>
              <TrendingUp className="w-4 h-4 text-tb-coral" />
            </div>
            <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {(clicks?.clicks || []).map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-tb-cream-3/60">
                  <div className="min-w-0">
                    <div className="font-serif-it text-tb-navy truncate">
                      {c.destination_name} <span className="font-hand text-tb-navy/50 text-xs">· {c.country}</span>
                    </div>
                    <div className="font-hand text-tb-navy/60 text-xs truncate">
                      by {c.user_name} ({c.user_email})
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-hand text-tb-coral text-sm font-semibold">+${40}</div>
                    <div className="font-ui text-[10px] text-tb-navy/50">{timeAgo(c.clicked_at)}</div>
                  </div>
                </li>
              ))}
              {(!clicks?.clicks || clicks.clicks.length === 0) && (
                <li className="font-hand text-tb-navy/50 text-center py-6">no clicks yet</li>
              )}
            </ul>
          </section>
        </div>

        {/* Redemption summary */}
        <section className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-tb-orange-soft flex items-center justify-center">
              <Gift className="w-5 h-5 text-tb-orange" />
            </div>
            <div>
              <h2 className="font-serif-it text-tb-navy text-xl">Rewards redeemed</h2>
              <p className="font-hand text-tb-navy/60 text-sm italic">
                {stats.redemptions_count} reward{stats.redemptions_count === 1 ? "" : "s"} claimed across all users
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="font-serif text-2xl text-tb-navy">{fmt(stats.redemptions_points)}</div>
            <div className="font-hand text-tb-navy/60 text-xs">pts spent · {fmt(stats.total_points_awarded)} awarded</div>
          </div>
        </section>

        {/* Digest card */}
        <section data-testid={ADMIN.digestCard}
          className="rounded-2xl border-2 border-tb-gold/60 p-5"
          style={{ background: "linear-gradient(135deg, #1a3a5f 0%, #2a4a72 60%, #1a3a5f 100%)" }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full bg-tb-gold/20 border border-tb-gold/60 flex items-center justify-center">
                <Mail className="w-5 h-5 text-tb-gold-bright" />
              </div>
              <div>
                <h2 className="font-serif-it text-tb-gold-bright text-2xl leading-tight">Weekly digest email</h2>
                <p className="font-hand text-tb-blue/90 text-sm italic">
                  Scheduled every <b className="text-tb-gold-bright">{digest?.schedule?.day_of_week?.toUpperCase()}</b> at
                  {" "}<b className="text-tb-gold-bright">
                    {String(digest?.schedule?.hour ?? 9).padStart(2, "0")}:
                    {String(digest?.schedule?.minute ?? 0).padStart(2, "0")} {digest?.schedule?.tz}
                  </b> · sent to all admins
                </p>
                {!digest?.resend_configured && (
                  <p className="mt-2 inline-flex items-center gap-2 text-xs font-ui text-tb-orange-soft bg-tb-orange/30 px-2.5 py-1 rounded-full">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    RESEND_API_KEY is a placeholder — add your real key in <code className="font-mono">backend/.env</code>
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid={ADMIN.digestPreviewBtn}
                onClick={openPreview}
                className="px-4 py-2 rounded-full bg-tb-cream/10 border-2 border-tb-gold/60 text-tb-gold-bright font-hand text-base tb-tap hover:bg-tb-cream/20 flex items-center gap-1.5"
              >
                <Eye className="w-4 h-4" /> Preview
              </button>
              <button
                data-testid={ADMIN.digestSendBtn}
                disabled={sending}
                onClick={sendDigestNow}
                className="px-4 py-2 rounded-full bg-tb-gold-bright text-tb-navy-deep font-hand text-base tb-tap disabled:opacity-60 flex items-center gap-1.5 shadow"
              >
                <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send now"}
              </button>
            </div>
          </div>

          {digest?.history?.length > 0 && (
            <div data-testid={ADMIN.digestHistory} className="mt-4 border-t border-tb-gold/30 pt-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Clock className="w-3.5 h-3.5 text-tb-blue" />
                <span className="font-hand text-tb-blue text-sm italic">recent sends</span>
              </div>
              <ul className="space-y-1.5">
                {digest.history.map((h) => (
                  <li key={h.id} className="flex items-center justify-between gap-2 text-xs font-ui">
                    <span className="text-tb-blue-soft">
                      {new Date(h.sent_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                      <span className="text-tb-blue/60"> · to {h.recipients?.length ?? 0} admin{(h.recipients?.length === 1) ? "" : "s"}</span>
                    </span>
                    <span className={
                      h.delivered
                        ? "px-2 py-0.5 rounded-full bg-tb-sage/30 text-white font-hand"
                        : "px-2 py-0.5 rounded-full bg-tb-orange/30 text-tb-orange-soft font-hand"
                    }>
                      {h.delivered ? `✓ ${h.sent_count ?? 0} sent` : "skipped"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <UsersRolesPanel />

        <ImportDestinationsCsv />


        <BookingComCjPanel />

        <DestinationMaintenance />

        {/* (2026-07-12) <MaintenanceActions /> removed —
            "Clean up stale booking notifications" was one-off cleanup
            for a deprecated Option-C bucket-picker flow. Endpoint
            `POST /api/admin/cleanup-stale-notifications` retained;
            call via curl if the situation ever repeats. */}

        <AddDestinationCard />

        <ReviewQueue />

        <p className="text-center font-hand text-tb-navy/40 italic text-sm pt-2 pb-6">
          Owner Compass · TravelBio · v1
        </p>
      </div>
    </div>
  );
}

function MaintenanceActions() {
  // (2026-07-12) UI removed — see comment at usage site above. Kept
  // as a no-op stub so any lingering import doesn't break; safe to
  // delete entirely in a follow-up cleanup PR.
  return null;
}


function DestinationMaintenance() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [lowConfCount, setLowConfCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [regeoBusy, setRegeoBusy] = useState(false);
  const [regeoResult, setRegeoResult] = useState(null);
  // State-backfill (Nominatim reverse geocode for rows missing `state`)
  const [stateBfDryRun, setStateBfDryRun] = useState(true);
  const [stateBfBusy, setStateBfBusy] = useState(false);
  const [stateBfJobId, setStateBfJobId] = useState(null);
  const [stateBfStatus, setStateBfStatus] = useState(null);   // full admin_jobs doc
  const [stateBfDrawerOpen, setStateBfDrawerOpen] = useState(false);
  const [polling, setPolling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [drawerBucket, setDrawerBucket] = useState(null);  // null | bucket string

  const load = async () => {
    try {
      // Fire in parallel — /stats drives the coral bar, /backfill-status
      // drives the coverage strip, and a tiny probe to /diagnostics gives
      // us the low-confidence count for the "For your review" pill.
      const [{ data: s }, { data: c }, { data: lc }] = await Promise.all([
        api.get("/admin/travelbio-destinations/stats"),
        api.get("/admin/travelbio-destinations/backfill-status"),
        api.get("/admin/travelbio-destinations/diagnostics", {
          params: { bucket: "low_confidence", limit: 1, offset: 0 },
        }),
      ]);
      setStats(s);
      setCoverage(c);
      setLowConfCount(lc?.total ?? 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load destination stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // On mount, opportunistically fetch the last completed state-backfill
  // job so the "View affected rows" button survives page reload without
  // requiring the admin to re-run the job.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/destinations/backfill-states/latest");
        if (cancelled) return;
        setStateBfJobId(data.job_id);
        setStateBfStatus(data);
      } catch (e) {
        // 404 is expected when no job has ever run — silent.
        if (e.response?.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn("latest state-backfill fetch failed", e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Polling loop: refresh stats every 20s while a backfill is running.
  useEffect(() => {
    if (!polling) return;
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [polling]);

  // Idle auto-refresh: while not actively polling a backfill, still
  // refresh the coverage strip every 60s so admins get a live glance
  // at photo/property-id coverage without navigating away.
  useEffect(() => {
    if (polling) return;
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [polling]);

  // Auto-stop polling once all rows are backfilled.
  useEffect(() => {
    if (polling && stats && stats.missing_stay22_property_id === 0) {
      setPolling(false);
      toast.success("Stay22 backfill complete — every destination now has an ID ✨");
    }
  }, [stats, polling]);

  const triggerReseed = async (force) => {
    setBusy(true);
    try {
      await api.post("/admin/travelbio-destinations/reseed", { force });
      toast.success("Backfill started — runs in the background, takes ~15 min. You can navigate away.");
      setPolling(true);
      // Nudge the stats after 5s so the user sees the counter refresh soon.
      setTimeout(load, 5000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start backfill");
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  const retryGeocoding = async () => {
    setRegeoBusy(true);
    setRegeoResult(null);
    try {
      const { data } = await api.post("/admin/travelbio-destinations/regeocode-missing");
      setRegeoResult(data);  // { fixed, still_missing }
      toast.success(
        `Regeocode done · ${data.fixed} fixed · ${data.still_missing} still missing`,
      );
      // Refresh the whole strip so pill counts + coverage catch up.
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Regeocode failed");
    } finally {
      setRegeoBusy(false);
    }
  };

  // -----------------------------------------------------------------
  // State backfill — reverse-geocodes rows that have lat/lng but no
  // `state` field (typically CSV rows where the `state_region` column
  // was blank). Runs as a FastAPI BackgroundTask; we poll every 5s
  // for the result. Idempotent: re-runs skip previously-backfilled
  // rows via the `booking_metadata.state_backfilled_at` timestamp.
  // -----------------------------------------------------------------
  const runStateBackfill = async () => {
    setStateBfBusy(true);
    setStateBfStatus(null);
    setStateBfJobId(null);
    try {
      const { data } = await api.post(
        `/admin/destinations/backfill-states?dry_run=${stateBfDryRun ? "true" : "false"}`,
      );
      const jobId = data.job_id;
      setStateBfJobId(jobId);
      toast.info(`${stateBfDryRun ? "Dry-run" : "Backfill"} started · job ${jobId}`);
      // Poll every 5 sec — bail after 5 min ceiling.
      const started = Date.now();
      const CEILING_MS = 5 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: doc } = await api.get(
            `/admin/destinations/backfill-states/${encodeURIComponent(jobId)}`,
          );
          setStateBfStatus(doc);
          if (doc.status && doc.status !== "running") {
            if (doc.status === "completed") {
              const r = doc.result || {};
              toast.success(
                `State backfill ${stateBfDryRun ? "(dry-run) " : ""}done · scanned ${r.scanned || 0}, updated ${r.updated || 0}, no-state ${r.no_state_available || 0}, failed ${r.reverse_geocode_failed || 0}`,
              );
            } else {
              toast.error(`Backfill failed: ${doc.error || "unknown"}`);
            }
            break;
          }
        } catch (pollErr) {
          // Poll failure is not fatal — keep trying until ceiling.
          // eslint-disable-next-line no-console
          console.warn("state-backfill poll error", pollErr);
        }
        if (Date.now() - started > CEILING_MS) {
          toast.error("State backfill polling timed out after 5 min");
          break;
        }
      }
      if (!stateBfDryRun) await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start backfill");
    } finally {
      setStateBfBusy(false);
    }
  };

  // -----------------------------------------------------------------
  // Repair Bad Locations — reverse-geocodes rows where the state
  // value is not a known subdivision of the country, or where a US
  // row has no state, or where a suspicious template city dominates
  // an import batch. Same polling pattern as the state backfill.
  // -----------------------------------------------------------------
  const [repairDryRun, setRepairDryRun] = useState(true);
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairJobId, setRepairJobId] = useState(null);
  const [repairStatus, setRepairStatus] = useState(null);
  const [repairDrawerOpen, setRepairDrawerOpen] = useState(false);
  const [repairDrawerRefresh, setRepairDrawerRefresh] = useState(0);
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);
  const [retryDryRun, setRetryDryRun] = useState(false);
  // (OTA30) Geocode missing locations — forward-geocodes every card
  // where lat/lng is null/0/missing. Idempotent via
  // `booking_metadata.geocoded_at`. Same polling pattern as Repair.
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoDryRun, setGeoDryRun] = useState(false);
  const [geoJobId, setGeoJobId] = useState(null);
  const [geoStatus, setGeoStatus] = useState(null);
  // (OTA33) Purge non-user cards — keeps only travelbio_id ~ /^TB[0-9]+$/
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeDryRun, setPurgeDryRun] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  // (2026-07-12) Heal state codes — normalizes every destination doc's
  // `state` field to the canonical 2-letter uppercase abbrev. Wired to
  // `POST /api/admin/destinations/heal-state-codes`. Idempotent.
  const [healStatesBusy, setHealStatesBusy] = useState(false);
  const [healStatesResult, setHealStatesResult] = useState(null);
  const runHealStateCodes = async () => {
    setHealStatesBusy(true);
    setHealStatesResult(null);
    try {
      const { data } = await api.post("/admin/destinations/heal-state-codes");
      setHealStatesResult(data);
      toast.success(
        `Healed ${data.updated || 0} of ${data.scanned || 0} destination state code${
          (data.scanned || 0) === 1 ? "" : "s"
        }`,
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Heal state codes failed");
    } finally {
      setHealStatesBusy(false);
    }
  };

  const runPurgeNonUser = async () => {
    if (!purgeDryRun) {
      const ok = window.confirm(
        "Purge non-user CSV cards?\n\n" +
        "This deletes every card in travelbio_destinations whose " +
        "travelbio_id does NOT match ^TB[0-9]+$ (your TB1, TB2, …). " +
        "It also drops the legacy `destinations`, `hotelbeds_destinations`, " +
        "and `hotelbeds_runs` collections.\n\n" +
        "Users, chapters, bookmarks, booked trips, dream vacations are NOT touched.\n\n" +
        "Continue?"
      );
      if (!ok) return;
    }
    setPurgeBusy(true);
    setPurgeResult(null);
    try {
      const { data } = await api.post(
        `/admin/destinations/purge-non-user-cards?dry_run=${purgeDryRun ? "true" : "false"}`,
      );
      setPurgeResult(data);
      if (data.dry_run) {
        toast.info(
          `Dry run · would delete ${data.would_delete?.travelbio_destinations_legacy_rows || 0} legacy rows.`,
        );
      } else {
        toast.success(
          `Purged ${data.deleted?.travelbio_destinations_legacy_rows || 0} legacy rows · kept ${data.keep?.travelbio_destinations_matching_TB_pattern || 0}.`,
        );
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Purge failed");
    } finally {
      setPurgeBusy(false);
    }
  };

  const runGeocodeMissing = async () => {
    setGeoBusy(true);
    setGeoStatus(null);
    setGeoJobId(null);
    try {
      const { data } = await api.post(
        `/admin/destinations/geocode-missing?dry_run=${geoDryRun ? "true" : "false"}`,
      );
      const jobId = data.job_id;
      setGeoJobId(jobId);
      toast.info(`${geoDryRun ? "Dry-run" : "Geocode missing"} started · job ${jobId}`);
      const started = Date.now();
      const CEILING_MS = 20 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 6000));
        try {
          const { data: doc } = await api.get(
            `/admin/destinations/geocode-missing/${encodeURIComponent(jobId)}`,
          );
          setGeoStatus(doc);
          if (doc.status && doc.status !== "running") {
            if (doc.status === "completed") {
              const r = doc.result || {};
              toast.success(
                `Geocode missing ${geoDryRun ? "(dry-run) " : ""}done · scanned ${r.total_missing_coords || 0}, updated ${r.updated || 0}, no coords ${r.no_coords_found || 0}, failed ${r.failed || 0}`,
              );
            } else {
              toast.error(`Geocode missing failed: ${doc.error || "unknown"}`);
            }
            break;
          }
        } catch (pollErr) {
          console.warn("geocode-missing poll error", pollErr);
        }
        if (Date.now() - started > CEILING_MS) {
          toast.info("Geocode missing job still running — check back later");
          break;
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start geocode-missing");
    } finally {
      setGeoBusy(false);
    }
  };

  // On mount, opportunistically fetch the last completed repair job.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/destinations/repair-bad-locations/latest");
        if (cancelled) return;
        setRepairJobId(data.job_id);
        setRepairStatus(data);
      } catch (e) {
        if (e.response?.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn("latest repair fetch failed", e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const runRepair = async () => {
    setRepairBusy(true);
    setRepairStatus(null);
    setRepairJobId(null);
    try {
      const { data } = await api.post(
        `/admin/destinations/repair-bad-locations?dry_run=${repairDryRun ? "true" : "false"}`,
      );
      const jobId = data.job_id;
      setRepairJobId(jobId);
      toast.info(`${repairDryRun ? "Dry-run" : "Repair"} started · job ${jobId}`);
      const started = Date.now();
      const CEILING_MS = 15 * 60 * 1000;  // 15 min ceiling — Nominatim is 1 req/s
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: doc } = await api.get(
            `/admin/destinations/repair-bad-locations/${encodeURIComponent(jobId)}`,
          );
          setRepairStatus(doc);
          if (doc.status && doc.status !== "running") {
            if (doc.status === "completed") {
              const r = doc.result || {};
              toast.success(
                `Repair ${repairDryRun ? "(dry-run) " : ""}done · scanned ${r.scanned || 0}, repaired ${r.repaired || r.would_repair || 0}, no-change ${r.no_change || 0}, failed ${r.failed || 0}`,
              );
            } else {
              toast.error(`Repair failed: ${doc.error || "unknown"}`);
            }
            break;
          }
        } catch (pollErr) {
          console.warn("repair poll error", pollErr);
        }
        if (Date.now() - started > CEILING_MS) {
          toast.error("Repair polling timed out after 15 min");
          break;
        }
      }
      if (!repairDryRun) await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start repair");
    } finally {
      setRepairBusy(false);
    }
  };

  // "Retry failed only" — reuses the source_job_id from the last run
  // so the backend rehydrates the failed-id list without re-scanning
  // the whole catalog. Falls back to the "all rows carrying a failure
  // stamp" query if the source job has expired. Same 5s polling
  // pattern as `runRepair`.
  const runRetryFailed = async () => {
    setRetryConfirmOpen(false);
    setRepairBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("dry_run", retryDryRun ? "true" : "false");
      if (repairJobId) params.set("source_job_id", repairJobId);
      const { data } = await api.post(
        `/admin/destinations/repair-bad-locations/retry-failed?${params.toString()}`,
      );
      const jobId = data.job_id;
      setRepairJobId(jobId);
      toast.info(
        `Retrying ${data.retry_count} previously-failed row${data.retry_count === 1 ? "" : "s"} · job ${jobId}`,
      );
      const started = Date.now();
      const CEILING_MS = 15 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: doc } = await api.get(
            `/admin/destinations/repair-bad-locations/${encodeURIComponent(jobId)}`,
          );
          setRepairStatus(doc);
          if (doc.status && doc.status !== "running") {
            if (doc.status === "completed") {
              const r = doc.result || {};
              toast.success(
                `Retry ${retryDryRun ? "(dry-run) " : ""}done · scanned ${r.scanned || 0}, repaired ${r.repaired || r.would_repair || 0}, no-change ${r.no_change || 0}, still failed ${r.failed || 0}`,
              );
              // Bump the refresh key so the drawer re-fetches its
              // currently-open bucket with the new failure stamps.
              setRepairDrawerRefresh((n) => n + 1);
            } else {
              toast.error(`Retry failed: ${doc.error || "unknown"}`);
            }
            break;
          }
        } catch (pollErr) {
          console.warn("retry poll error", pollErr);
        }
        if (Date.now() - started > CEILING_MS) {
          toast.error("Retry polling timed out after 15 min");
          break;
        }
      }
      if (!retryDryRun) await load();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 404) {
        toast.info(msg || "No previously-failed rows to retry.");
      } else {
        toast.error(msg || "Couldn't start retry");
      }
    } finally {
      setRepairBusy(false);
    }
  };

  const total = stats?.total ?? 0;
  const withPid = stats?.with_stay22_property_id ?? 0;
  const missingPid = stats?.missing_stay22_property_id ?? 0;
  const missingImg = stats?.missing_featured_image ?? 0;
  const pct = total > 0 ? Math.round((withPid / total) * 100) : 0;
  const allDone = total > 0 && missingPid === 0;

  // Coverage strip values (from /backfill-status — different semantics
  // from /stats: `missing` here INCLUDES `unfindable`).
  const covTotal = coverage?.total ?? 0;
  const covPhoto = coverage?.with_featured_image ?? 0;
  const covAwaiting = coverage?.awaiting_lookup ?? 0;
  const covUnfindable = coverage?.unfindable_stay22_property_id ?? 0;
  const photoPct = covTotal > 0 ? Math.round((covPhoto / covTotal) * 100) : 0;

  return (
    <section
      data-testid={ADMIN.destMaintCard}
      className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-tb-blue/15 border border-tb-blue/40 flex items-center justify-center">
            <Database className="w-5 h-5 text-tb-blue" />
          </div>
          <div>
            <h2 className="font-serif-it text-tb-navy text-2xl leading-tight">Destination Maintenance</h2>
            <p className="font-hand text-tb-navy/60 text-sm italic">
              Curated TravelBio catalog · Booking.com CJ URL coverage
            </p>
          </div>
        </div>
        {polling && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-tb-blue/15 border border-tb-blue/40 text-tb-blue font-hand text-xs">
            <Loader2 className="w-3 h-3 animate-spin" /> backfilling · polling every 20s
          </span>
        )}
      </div>

      {loading || !stats ? (
        <p className="font-hand text-tb-navy/55 italic text-sm">loading destination stats…</p>
      ) : (
        <>
          {/* (OTA32) Stay22 coverage strip removed 2026-07-11 —
              Stay22 dropped as a booking provider. The Destination
              Maintenance controls (Repair Bad Locations, Geocode
              Missing Locations, Backfill missing states) below remain
              because they are provider-agnostic. */}
          {false && (
          <div
            data-testid={ADMIN.destMaintStats}
            className="rounded-xl bg-tb-cream-2/70 border border-tb-cream-3 p-4 mb-4"
          >
            <div className="flex items-baseline justify-between mb-2">
              <span className="font-serif text-tb-navy text-xl leading-none">
                <b className="text-tb-coral">{withPid.toLocaleString()}</b>
                <span className="text-tb-navy/60"> of </span>
                <b>{total.toLocaleString()}</b>
                <span className="font-hand text-tb-navy/70 text-base"> destinations have Stay22 IDs</span>
              </span>
              <span className="font-hand text-tb-navy/70 text-sm">{pct}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-tb-cream-3 overflow-hidden">
              <div
                className="h-full bg-tb-coral transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-2 font-hand text-xs">
              {/* (OTA32) 'missing IDs' pill removed 2026-07-11 — Stay22 dropped. */}
              {false && (
              <button
                type="button"
                data-testid="admin-dest-maint-missing-ids-pill"
                onClick={() => setDrawerBucket("missing_ids")}
                disabled={missingPid === 0}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-coral/10 border border-tb-coral/40 text-tb-coral hover:bg-tb-coral/20 transition disabled:opacity-40 disabled:cursor-default"
              >
                <MapPin className="w-3 h-3" />
                {missingPid.toLocaleString()} missing IDs
              </button>
              )}
              <button
                type="button"
                data-testid="admin-dest-maint-missing-images-pill"
                onClick={() => setDrawerBucket("missing_images")}
                disabled={missingImg === 0}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-cream border border-tb-cream-3 text-tb-navy/70 hover:bg-tb-cream-3/60 transition disabled:opacity-40 disabled:cursor-default"
              >
                <ImageOff className="w-3 h-3" />
                {missingImg.toLocaleString()} missing images
              </button>
            </div>
          </div>
          )}

          {/* (OTA32) Stay22 photo-coverage strip removed 2026-07-11 —
              Stay22 dropped as a booking provider. See
              /app/memory/INTEGRATIONS_REMOVAL.md. */}
          {false && coverage && (
            <div
              data-testid="admin-dest-maint-coverage"
              className="rounded-xl bg-tb-cream-2/70 border border-tb-cream-3 p-4 mb-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <span className="font-serif text-tb-navy text-lg leading-none">
                  Stay22 photo coverage
                </span>
                <span className="font-hand text-tb-navy/70 text-sm">
                  <b>{photoPct}%</b>
                  <span className="text-tb-navy/50"> · </span>
                  {covPhoto.toLocaleString()} of {covTotal.toLocaleString()} with real photos
                </span>
              </div>
              <div className="w-full h-2 rounded-full bg-tb-cream-3 overflow-hidden">
                <div
                  className="h-full bg-tb-gold transition-[width] duration-500 ease-out"
                  style={{ width: `${photoPct}%` }}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2 items-center font-hand text-xs">
                <span
                  data-testid="admin-dest-maint-total-pill"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-cream border border-tb-cream-3 text-tb-navy/70"
                >
                  Total: <b className="text-tb-navy">{covTotal.toLocaleString()}</b>
                </span>
                {covAwaiting > 0 && (
                  <button
                    type="button"
                    data-testid="admin-dest-maint-awaiting-pill"
                    onClick={() => setDrawerBucket("awaiting_lookup")}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-gold/20 border border-tb-gold/50 text-tb-navy hover:bg-tb-gold/30 transition"
                  >
                    <RotateCw className="w-3 h-3" />
                    Awaiting lookup: <b>{covAwaiting.toLocaleString()}</b>
                  </button>
                )}
                {covUnfindable > 0 && (
                  <button
                    type="button"
                    data-testid="admin-dest-maint-unfindable-pill"
                    onClick={() => setDrawerBucket("unfindable")}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-cream border border-tb-cream-3 text-tb-navy/70 italic hover:bg-tb-cream-3/60 transition"
                  >
                    <Ban className="w-3 h-3" />
                    Unfindable (no Stay22 match): <b className="not-italic text-tb-navy/80">{covUnfindable.toLocaleString()}</b>
                  </button>
                )}
                <span className="font-hand text-tb-navy/40 italic ml-auto">
                  auto-refreshes every 60s
                </span>
              </div>
            </div>
          )}

          {/* "For your review" — surfaces cards flagged as low-confidence
              (photo host outside trusted list OR nominatim geocode
              approximated). Empty state hides the whole card so the panel
              stays tidy when there's nothing to review. */}
          {lowConfCount > 0 && (
            <div
              data-testid="admin-dest-maint-review"
              className="rounded-xl bg-tb-cream-2/70 border border-tb-gold/60 p-4 mb-4"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2.5">
                  <ShieldAlert className="w-4 h-4 text-tb-gold mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-serif text-tb-navy text-base leading-tight">For your review</div>
                    <div className="font-hand text-tb-navy/60 italic text-xs mt-0.5">
                      Cards with an untrusted photo host or an approximate geocode.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="admin-dest-maint-low-confidence-pill"
                  onClick={() => setDrawerBucket("low_confidence")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tb-gold/25 border border-tb-gold/60 text-tb-navy font-hand text-sm hover:bg-tb-gold/40 transition"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {lowConfCount.toLocaleString()} low-confidence match{lowConfCount === 1 ? "" : "es"}
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {/* (OTA32) 'Backfill Missing Stay22 IDs' button removed 2026-07-11.
                Stay22 has been dropped as a booking provider — cards are now
                photo-populated manually via CSV/admin form. See
                /app/memory/INTEGRATIONS_REMOVAL.md for reactivation. */}
            <button
              data-testid="admin-dest-maint-regeocode-btn"
              disabled={regeoBusy}
              onClick={retryGeocoding}
              className="px-4 py-2 rounded-full bg-tb-blue text-white font-hand text-base tb-tap disabled:opacity-50 flex items-center gap-1.5 shadow hover:brightness-110 transition"
              title="Retry Nominatim on rows still flagged needs_geocode: true"
            >
              {regeoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPinOff className="w-4 h-4" />}
              Retry geocoding on missing rows
            </button>
            <button
              data-testid={ADMIN.destMaintForceBtn}
              disabled={busy}
              onClick={() => setConfirmOpen(true)}
              className="px-4 py-2 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 text-tb-navy font-hand text-base tb-tap disabled:opacity-60 flex items-center gap-1.5 hover:bg-tb-cream-3/60 transition"
            >
              Force Full Reseed
            </button>
          </div>

          {/* State backfill — reverse-geocodes rows missing `state`.
              (2026-07-12) UI removed; endpoint retained. */}

          {regeoResult && (
            <div
              data-testid="admin-dest-maint-regeocode-result"
              className="mt-3 rounded-lg border border-tb-blue/40 bg-tb-blue/10 p-3 font-hand text-tb-navy text-sm"
            >
              Regeocode summary — <b>{regeoResult.fixed}</b> fixed ·{" "}
              <b>{regeoResult.still_missing}</b> still missing
            </div>
          )}

          {/* (2026-07-12) Removed dead maintenance widgets:
                - "Backfill missing states" button + dry-run + summary
                - "Repair bad locations" button + dry-run + summary
                - "Geocode missing locations" button + dry-run + summary
                - "Purge non-user CSV cards" button + dry-run + summary
              Underlying endpoints are still callable via curl for edge
              cases; the UI was clutter for day-to-day admin work.
              See @deprecated markers on the backend routes and the
              (still-declared) local state hooks below. */}

          <div className="mt-3 flex flex-wrap items-center gap-3 pt-3 border-t border-tb-cream-3/60">
            <button
              type="button"
              data-testid="admin-dest-maint-heal-state-codes-btn"
              disabled={healStatesBusy}
              onClick={runHealStateCodes}
              className="px-4 py-2 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-base tb-tap disabled:opacity-60 flex items-center gap-1.5 shadow hover:brightness-110 transition"
              title="Rewrite every destination's `state` field to the canonical 2-letter uppercase abbrev (VT / NH / CA…). Idempotent."
            >
              {healStatesBusy
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <MapPinned className="w-4 h-4" />}
              Heal state codes
            </button>
            {healStatesResult && (
              <span className="font-hand text-tb-navy/60 text-xs italic">
                healed <b className="not-italic text-tb-navy">{healStatesResult.updated}</b>
                {" of "}<b className="not-italic text-tb-navy">{healStatesResult.scanned}</b>
              </span>
            )}
          </div>
          {healStatesResult?.samples?.length > 0 && (
            <details
              data-testid="admin-dest-maint-heal-state-codes-samples"
              className="mt-2 rounded-lg border border-tb-cream-3 bg-tb-cream-2/40 p-3"
            >
              <summary className="cursor-pointer font-hand text-tb-navy text-sm">
                View first {Math.min(5, healStatesResult.samples.length)} change{healStatesResult.samples.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-2 space-y-1 font-hand text-tb-navy/80 text-xs">
                {healStatesResult.samples.slice(0, 5).map((s, i) => (
                  <li key={s.id || i} className="flex items-baseline gap-2">
                    <span className="truncate">{s.name}</span>
                    <span className="text-tb-navy/50">
                      <b className="not-italic text-tb-navy/70">{s.before}</b>
                      {" \u2192 "}
                      <b className="not-italic text-tb-navy">{s.after}</b>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {/* Drill-down drawer — opened by any of the clickable pills. */}
      <DiagnosticsDrawer
        bucket={drawerBucket}
        onClose={() => setDrawerBucket(null)}
        onEdit={(id) => { setDrawerBucket(null); nav(`/admin/destinations/${id}`); }}
        onChanged={load}
      />

      {/* State-backfill affected-rows drill-down. */}
      <StateBackfillRowsDrawer
        open={stateBfDrawerOpen}
        jobId={stateBfJobId}
        counts={stateBfStatus?.result?.affected_ids || {}}
        onClose={() => setStateBfDrawerOpen(false)}
        onEdit={(id) => { setStateBfDrawerOpen(false); nav(`/admin/destinations/${id}`); }}
      />

      {/* Repair-Bad-Locations affected-rows drill-down. */}
      <RepairBadLocationsRowsDrawer
        open={repairDrawerOpen}
        jobId={repairJobId}
        counts={repairStatus?.result?.affected_ids || {}}
        refreshKey={repairDrawerRefresh}
        onClose={() => setRepairDrawerOpen(false)}
        onEdit={(id) => { setRepairDrawerOpen(false); nav(`/admin/destinations/${id}`); }}
      />

      {/* Repair-Bad-Locations retry-failed confirm dialog. */}
      <AlertDialog open={retryConfirmOpen} onOpenChange={setRetryConfirmOpen}>
        <AlertDialogContent data-testid="admin-repair-retry-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Retry {repairStatus?.result?.failed || 0} previously-failed row{(repairStatus?.result?.failed || 0) === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This re-runs Nominatim lookups on the failed rows from the
              last repair pass, using the improved forward-geocode
              fallback chain. Nominatim is capped at 1.1&nbsp;req/sec, so
              expect roughly {" "}
              <b>~{Math.max(Math.round((repairStatus?.result?.failed || 0) * 1.5), 1)} seconds</b> {" "}
              of runtime (each row may make multiple lookups). Rows that
              succeed on retry lose their failure stamp; rows that fail
              again keep it — safe to click repeatedly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-center gap-2 font-hand text-tb-navy/80 text-sm cursor-pointer my-2">
            <input
              type="checkbox"
              data-testid="admin-repair-retry-dryrun"
              checked={retryDryRun}
              onChange={(e) => setRetryDryRun(e.target.checked)}
              className="w-4 h-4 accent-tb-navy cursor-pointer"
            />
            Dry run (don't persist changes)
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-repair-retry-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-repair-retry-confirm"
              onClick={runRetryFailed}
              className="bg-tb-orange text-white hover:bg-tb-orange/90"
            >
              Retry {repairStatus?.result?.failed || 0} row{(repairStatus?.result?.failed || 0) === 1 ? "" : "s"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={ADMIN.destMaintConfirmDialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Force full reseed?</AlertDialogTitle>
            <AlertDialogDescription>
              This re-runs the canonical destinations seed AND kicks off a full Stay22 property-ID
              backfill for every row. Safe (idempotent per-row) but takes ~15 min in the background.
              You can navigate away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.destMaintConfirmCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={ADMIN.destMaintConfirmYes}
              onClick={() => triggerReseed(true)}
              className="bg-tb-coral hover:brightness-110"
            >
              Yes, force reseed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}


// ---------------------------------------------------------------------
// DiagnosticsDrawer — opened by any of the clickable coverage pills or
// the low-confidence review pill. Lists cards in the requested bucket
// with per-row Edit / Delete actions.
//
// `bucket === null` closes the drawer. Edit navigates to the existing
// admin edit page (`/admin/destinations/:id`). Delete uses the existing
// admin delete endpoint (403s on seed rows — we surface that clearly
// rather than force-relaxing the seed protection).
// ---------------------------------------------------------------------
const BUCKET_LABELS = {
  missing_ids:     { title: "Missing Stay22 IDs",           note: "Cards without a resolved Stay22 property_id (excluding unfindable)." },
  awaiting_lookup: { title: "Awaiting Stay22 lookup",        note: "Same as missing IDs — cards still eligible for backfill." },
  missing_images:  { title: "Missing images",                note: "Cards with no photo or a placehold.co placeholder." },
  unfindable:      { title: "Unfindable · no Stay22 match",  note: "Stay22 returned no match for these cards. Edit or delete." },
  low_confidence:  { title: "Low-confidence matches",        note: "Photo host untrusted or geocode resolved to state/country level." },
};

function DiagnosticsDrawer({ bucket, onClose, onEdit, onChanged }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);  // card object
  const [retryingId, setRetryingId] = useState(null);         // card.id in-flight
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  // Bulk-select for the drawer's own rows. Only deletable rows
  // (source == "manual") can be added to this set; seed rows are
  // rendered without a checkbox so the state stays clean.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Batch-eligible buckets — the retry-stay22 endpoint only makes sense
  // for cards whose bucket state is a function of Stay22 lookup outcomes.
  // Missing-image + low-confidence buckets need different remediation
  // (photo re-fetch, manual review) so we hide the batch button there.
  const BATCH_ELIGIBLE = new Set(["missing_ids", "awaiting_lookup", "unfindable"]);
  const canBatch = BATCH_ELIGIBLE.has(bucket) && items.length > 0 && !batchRunning;

  // Clear selection whenever the bucket changes — different bucket ==
  // different row set, so stale IDs would leak across.
  useEffect(() => { setSelectedIds(new Set()); }, [bucket]);

  // Drawer-scoped deletion uses `/admin/destinations/bulk-delete`, which
  // is permissive (no `source == "manual"` gate). That endpoint powers
  // both single-row Delete and Delete-N-selected here, so every visible
  // row is selectable regardless of source. The `is_deletable` field
  // still exists on the payload for backwards compatibility with other
  // callers, but we intentionally ignore it inside the drawer.
  const deletableIds = useMemo(() => items.map((it) => it.id), [items]);
  const selectableCount = deletableIds.length;
  const allSelectableSelected = selectableCount > 0 && deletableIds.every((id) => selectedIds.has(id));
  const selectionSize = selectedIds.size;

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllSelectable = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectableSelected) {
        deletableIds.forEach((id) => next.delete(id));
      } else {
        deletableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const reload = async () => {
    try {
      const { data } = await api.get(
        "/admin/travelbio-destinations/diagnostics",
        { params: { bucket, limit: 100, offset: 0 } },
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't reload diagnostics");
      return null;
    }
  };

  useEffect(() => {
    if (!bucket) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          "/admin/travelbio-destinations/diagnostics",
          { params: { bucket, limit: 100, offset: 0 } },
        );
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load diagnostics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bucket]);

  const retryStay22 = async (card) => {
    setRetryingId(card.id);
    try {
      await api.post(
        `/admin/travelbio-destinations/${encodeURIComponent(card.id)}/retry-stay22`,
      );
      toast.success(`Re-lookup complete — "${card.name}"`);
      // Refresh the drawer's list; the row may drop out of the current
      // bucket if the retry resolved its issue.
      await reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Re-lookup failed");
    } finally {
      setRetryingId(null);
    }
  };

  const runBatchRetry = async () => {
    setBatchConfirmOpen(false);
    if (batchRunning) return;
    setBatchRunning(true);
    // Snapshot the current bucket — items may drop out mid-run as
    // retries resolve. The snapshot is what the user confirmed against.
    const targets = [...items];
    const initialTotal = total;
    const toastId = "diag-batch-retry";
    toast.loading(`Retrying 0 of ${targets.length}…`, { id: toastId });

    let succeeded = 0;
    let failed = 0;
    for (let i = 0; i < targets.length; i++) {
      const card = targets[i];
      try {
        await api.post(
          `/admin/travelbio-destinations/${encodeURIComponent(card.id)}/retry-stay22`,
        );
        succeeded++;
      } catch (e) {
        failed++;
        // eslint-disable-next-line no-console
        console.warn("batch retry-stay22 failed", card.id, e?.response?.data || e);
      }
      // Update the loading toast every 5 completions (and always on the last).
      if ((i + 1) % 5 === 0 || i === targets.length - 1) {
        toast.loading(`Retrying ${i + 1} of ${targets.length}…`, { id: toastId });
      }
      // Rate limit: 500ms between calls (~2 req/sec) — matches the
      // per-row Stay22 lookup pace the backend expects.
      if (i < targets.length - 1) {
        await new Promise((r) => setTimeout(r, 500));
      }
    }

    toast.loading("Done. Refreshing list…", { id: toastId });
    const fresh = await reload();
    onChanged && onChanged();
    const finalTotal = fresh?.total ?? total;
    const resolved = Math.max(0, initialTotal - finalTotal - failed);
    const stillMissing = Math.max(0, finalTotal);
    toast.success(
      `Retried ${targets.length}: ${resolved} resolved, ${stillMissing} still missing${failed ? `, ${failed} failed` : ""}.`,
      { id: toastId, duration: 6000 },
    );
    setBatchRunning(false);
  };

  const runBulkDelete = async () => {
    setBulkDeleteConfirmOpen(false);
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    const toastId = "diag-bulk-delete";
    toast.loading(`Deleting ${ids.length} card${ids.length === 1 ? "" : "s"}…`, { id: toastId });
    try {
      const { data } = await api.post("/admin/destinations/bulk-delete", { ids });
      const failedCount = data.failed?.length || 0;
      toast[failedCount > 0 ? "error" : "success"](
        failedCount > 0
          ? `Deleted ${data.deleted}, ${failedCount} failed`
          : `Deleted ${data.deleted} card${data.deleted === 1 ? "" : "s"}`,
        { id: toastId, duration: 4500 },
      );
      setSelectedIds(new Set());
      // Reload the drawer — the deleted rows drop out — but DO NOT close it.
      await reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed", { id: toastId });
    } finally {
      setBulkDeleting(false);
    }
  };

  const confirmDelete = async () => {
    const card = pendingDelete;
    if (!card) return;
    try {
      // Route single-row delete through the same permissive bulk endpoint
      // the drawer's "Delete N selected" flow uses. Keeps behavior
      // consistent (works for CSV-imported seed_csv_* rows too) and
      // fixes the historical 403 users hit when trying to remove any
      // non-manual row from the drawer.
      const { data } = await api.post("/admin/destinations/bulk-delete", { ids: [card.id] });
      if ((data.failed || []).length > 0) {
        toast.error(`Delete failed — ${data.failed[0]?.reason || "unknown reason"}`);
      } else {
        toast.success(`Deleted "${card.name}"`);
        setItems((xs) => xs.filter((x) => x.id !== card.id));
        setTotal((n) => Math.max(0, n - 1));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(card.id);
          return next;
        });
        onChanged && onChanged();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setPendingDelete(null);
    }
  };

  const info = BUCKET_LABELS[bucket] || { title: bucket || "", note: "" };

  return (
    <>
      <Sheet open={!!bucket} onOpenChange={(v) => { if (!v) onClose(); }}>
        <SheetContent
          side="right"
          data-testid="admin-diagnostics-drawer"
          className="w-full sm:max-w-xl overflow-y-auto bg-tb-cream border-l-2 border-tb-cream-3"
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <SheetHeader className="text-left mb-3">
            <div className="flex items-start justify-between gap-3 pr-6">
              <div className="min-w-0 flex-1">
                <SheetTitle className="font-serif-it text-tb-navy text-2xl">
                  {info.title}
                </SheetTitle>
                <SheetDescription className="font-hand text-tb-navy/60 italic">
                  {info.note} · <b>{total.toLocaleString()}</b> card{total === 1 ? "" : "s"}
                  {items.length < total && ` · showing first ${items.length}`}
                </SheetDescription>
              </div>
              {/* (OTA32) 'Batch Retry Stay22' button removed 2026-07-11. */}
              {false && BATCH_ELIGIBLE.has(bucket) && total > 0 && (
                <button
                  type="button"
                  data-testid="admin-diagnostics-batch-retry-btn"
                  disabled={!canBatch}
                  onClick={() => setBatchConfirmOpen(true)}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tb-blue text-white font-hand text-sm shadow hover:brightness-110 transition disabled:opacity-60 disabled:cursor-wait"
                  title="Sequences Stay22 re-lookup for every card in this bucket, 500ms apart"
                >
                  {batchRunning
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <RefreshCw className="w-3.5 h-3.5" />}
                  Retry all {items.length} card{items.length === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </SheetHeader>

          {loading ? (
            <div className="flex items-center gap-2 py-6 font-hand text-tb-navy/60 italic">
              <Loader2 className="w-4 h-4 animate-spin" /> loading…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-xl bg-tb-cream-2/70 border border-tb-cream-3 p-6 text-center font-hand text-tb-navy/60 italic">
              Nothing in this bucket — nice.
            </div>
          ) : (
            <>
              {/* Bulk-select toolbar — only rows with is_deletable have
                  a checkbox, so this shows nothing when the bucket is
                  entirely seed rows. */}
              {selectableCount > 0 && (
                <div
                  data-testid="admin-diagnostics-bulk-toolbar"
                  className="flex flex-wrap items-center gap-2 px-3 py-2 mb-2 rounded-xl bg-tb-cream-2/70 border border-tb-cream-3"
                >
                  <label className="inline-flex items-center gap-2 font-hand text-tb-navy/80 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="admin-diagnostics-select-all"
                      checked={allSelectableSelected}
                      onChange={toggleAllSelectable}
                      className="w-4 h-4 accent-tb-navy cursor-pointer"
                    />
                    Select all {selectableCount} deletable
                  </label>
                  <div className="ml-auto flex items-center gap-2">
                    {selectionSize > 0 && (
                      <button
                        type="button"
                        data-testid="admin-diagnostics-clear-selection"
                        onClick={() => setSelectedIds(new Set())}
                        className="px-2.5 py-1 rounded-full bg-tb-cream border border-tb-cream-3 text-tb-navy/60 font-hand text-xs hover:text-tb-navy transition"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid="admin-diagnostics-bulk-delete-btn"
                      disabled={selectionSize === 0 || bulkDeleting}
                      onClick={() => setBulkDeleteConfirmOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tb-gold text-tb-navy font-hand text-sm shadow hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {bulkDeleting
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Trash2 className="w-3.5 h-3.5" />}
                      Delete {selectionSize} selected
                    </button>
                  </div>
                </div>
              )}

            <ul
              data-testid="admin-diagnostics-list"
              className="divide-y divide-tb-cream-3/60 rounded-xl bg-tb-cream-2/50 border border-tb-cream-3 overflow-hidden"
            >
              {items.map((it) => {
                const missingPhoto = it.has_placeholder_photo || !it.featured_image;
                return (
                  <li
                    key={it.id}
                    data-testid={`admin-diagnostics-item-${it.id}`}
                    className={`flex items-start gap-3 p-3 ${selectedIds.has(it.id) ? "bg-tb-gold/15" : ""}`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`admin-diagnostics-select-${it.id}`}
                      checked={selectedIds.has(it.id)}
                      onChange={() => toggleRowSelection(it.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-5 w-4 h-4 accent-tb-navy cursor-pointer flex-shrink-0"
                      aria-label={`Select ${it.name}`}
                    />
                    <div className="w-14 h-14 rounded-lg bg-tb-cream-3 flex-shrink-0 overflow-hidden flex items-center justify-center">
                      {missingPhoto ? (
                        <ImageOff className="w-5 h-5 text-tb-navy/40" />
                      ) : (
                        <img
                          src={it.featured_image}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-tb-navy text-sm leading-tight truncate">
                        {it.name || "(no name)"}
                      </div>
                      <div className="font-hand text-tb-navy/55 text-xs italic">
                        {[it.city, it.state, it.country].filter(Boolean).join(", ")}
                        {it.content_type ? ` · ${it.content_type}` : ""}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {bucket === "missing_ids" || bucket === "awaiting_lookup" ? (
                          <span className="px-1.5 py-0.5 rounded bg-tb-coral/15 border border-tb-coral/40 text-tb-coral text-[10px] font-hand">Missing Stay22 ID</span>
                        ) : null}
                        {bucket === "missing_images" ? (
                          <span className="px-1.5 py-0.5 rounded bg-tb-cream border border-tb-cream-3 text-tb-navy/60 text-[10px] font-hand">Missing image</span>
                        ) : null}
                        {bucket === "unfindable" ? (
                          <span className="px-1.5 py-0.5 rounded bg-tb-navy/10 border border-tb-navy/25 text-tb-navy/70 text-[10px] font-hand italic">Unfindable</span>
                        ) : null}
                        {bucket === "low_confidence" && it.reasons && it.reasons.map((r, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded bg-tb-gold/20 border border-tb-gold/50 text-tb-navy text-[10px] font-hand">{r}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button
                        type="button"
                        data-testid={`admin-diagnostics-edit-${it.id}`}
                        onClick={() => onEdit(it.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-tb-cream border border-tb-cream-3 text-tb-navy font-hand text-xs hover:bg-tb-cream-3/60 transition"
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      {/* (OTA32) 'Retry Stay22' per-row button removed 2026-07-11. */}
                      {false && (
                      <button
                        type="button"
                        data-testid={`admin-diagnostics-retry-${it.id}`}
                        disabled={retryingId === it.id}
                        onClick={() => retryStay22(it)}
                        title="Force a fresh Stay22 property-ID + photo lookup for this card"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-tb-blue/15 border border-tb-blue/40 text-tb-blue font-hand text-xs hover:bg-tb-blue/25 transition disabled:opacity-60 disabled:cursor-wait"
                      >
                        {retryingId === it.id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <RefreshCw className="w-3 h-3" />}
                        Retry Stay22
                      </button>
                      )}
                      <button
                        type="button"
                        data-testid={`admin-diagnostics-delete-${it.id}`}
                        onClick={() => setPendingDelete(it)}
                        title="Delete this card from the catalog"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-tb-orange/15 border border-tb-orange/40 text-tb-orange-soft font-hand text-xs hover:bg-tb-orange/25 transition"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => { if (!v) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="admin-diagnostics-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this destination?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently remove <b>{pendingDelete?.name}</b> ({pendingDelete?.city}, {pendingDelete?.state}) from the catalog. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-tb-orange hover:brightness-110">
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={batchConfirmOpen} onOpenChange={(v) => { if (!v) setBatchConfirmOpen(false); }}>
        <AlertDialogContent data-testid="admin-diagnostics-batch-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Retry Stay22 lookup on all {items.length} cards?</AlertDialogTitle>
            <AlertDialogDescription>
              Sequences one re-lookup every 500 ms — about {Math.max(1, Math.round((items.length * 0.5) / 60))} min for {items.length} cards.
              You can leave the drawer open; a progress toast will update as each row completes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runBatchRetry} className="bg-tb-blue hover:brightness-110">
              Yes, retry all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={(v) => { if (!v) setBulkDeleteConfirmOpen(false); }}>
        <AlertDialogContent data-testid="admin-diagnostics-bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectionSize} card{selectionSize === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected destinations from the catalog. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runBulkDelete} className="bg-tb-orange hover:brightness-110">
              Yes, delete {selectionSize}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


// ---------------------------------------------------------------------
// StateBackfillRowsDrawer — drill-down of rows a state-backfill job
// actually touched. Three tabs (Updated / No state / Failed), each
// backed by `GET /admin/destinations/backfill-states/{job_id}/rows`.
// Per-row Edit navigates to the shared destination editor route; Delete
// uses the permissive bulk-delete-with-1-id endpoint (same pattern the
// DiagnosticsDrawer already uses so seed rows aren't blocked). Copy IDs
// action at the tab header writes the current bucket's travelbio_id
// (falling back to id) list to the clipboard, newline separated.
// ---------------------------------------------------------------------
function StateBackfillRowsDrawer({ open, jobId, counts, onClose, onEdit }) {
  const [tab, setTab] = useState("updated");  // updated | no_state | failed
  const [items, setItems] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const bucketCount = (b) => (counts?.[b] || []).length;

  useEffect(() => {
    if (!open || !jobId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/admin/destinations/backfill-states/${encodeURIComponent(jobId)}/rows`,
          { params: { bucket: tab } },
        );
        if (cancelled) return;
        setItems(data.items || []);
        setTruncated(!!data.truncated);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load rows");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, jobId, tab]);

  const copyIds = async () => {
    if (items.length === 0) return;
    const ids = items.map((it) => it.travelbio_id || it.id).filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(ids);
      toast.success(`Copied ${items.length} id${items.length === 1 ? "" : "s"} to clipboard`);
    } catch {
      toast.error("Clipboard write blocked — copy the IDs manually from the URL bar or console");
    }
  };

  const confirmDelete = async () => {
    const card = pendingDelete;
    if (!card) return;
    try {
      const { data } = await api.post("/admin/destinations/bulk-delete", { ids: [card.id] });
      if ((data.failed || []).length > 0) {
        toast.error(`Delete failed — ${data.failed[0]?.reason || "unknown"}`);
      } else {
        toast.success(`Deleted "${card.name}"`);
        setItems((xs) => xs.filter((x) => x.id !== card.id));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setPendingDelete(null);
    }
  };

  const tabDefs = [
    { key: "updated",  label: "Updated" },
    { key: "no_state", label: "No state resolved" },
    { key: "failed",   label: "Failed" },
  ];

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <SheetContent
          side="right"
          data-testid="admin-state-backfill-drawer"
          className="w-full sm:max-w-2xl overflow-y-auto bg-tb-cream border-l-2 border-tb-cream-3"
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <SheetHeader className="text-left mb-3">
            <SheetTitle className="font-serif-it text-tb-navy text-2xl">
              State-backfill results
            </SheetTitle>
            <SheetDescription className="font-hand text-tb-navy/60 italic">
              job {jobId || "(none)"} · rows touched by the latest backfill
            </SheetDescription>
          </SheetHeader>

          {/* Tabs */}
          <div
            role="tablist"
            aria-label="Backfill bucket"
            className="inline-flex rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 p-1 mb-3"
          >
            {tabDefs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                data-testid={`admin-state-backfill-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={
                  tab === t.key
                    ? "px-3 py-1 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm"
                    : "px-3 py-1 rounded-full text-tb-navy/60 font-hand text-sm hover:text-tb-navy"
                }
              >
                {t.label} ({bucketCount(t.key)})
              </button>
            ))}
          </div>

          <div className="mb-2 flex flex-wrap items-center gap-3 font-hand text-xs">
            <button
              type="button"
              data-testid="admin-state-backfill-copy-ids"
              onClick={copyIds}
              disabled={items.length === 0}
              className="text-tb-navy underline decoration-tb-gold underline-offset-2 hover:decoration-2 disabled:opacity-40 disabled:no-underline"
            >
              Copy {items.length} id{items.length === 1 ? "" : "s"}
            </button>
            {truncated && (
              <span className="text-tb-orange-soft italic">
                (bucket capped at 1000 — showing the first 1000)
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-6 font-hand text-tb-navy/60 italic">
              <Loader2 className="w-4 h-4 animate-spin" /> loading…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-xl bg-tb-cream-2/70 border border-tb-cream-3 p-6 text-center font-hand text-tb-navy/60 italic">
              No cards in this bucket.
            </div>
          ) : (
            <ul
              data-testid="admin-state-backfill-list"
              className="divide-y divide-tb-cream-3/60 rounded-xl bg-tb-cream-2/50 border border-tb-cream-3 overflow-hidden"
            >
              {items.map((it) => (
                <li
                  key={it.id}
                  data-testid={`admin-state-backfill-row-${it.id}`}
                  className="flex items-start gap-3 p-3"
                >
                  <div className="w-12 h-12 rounded-lg bg-tb-cream-3 flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {it.thumb && !String(it.thumb).toLowerCase().includes("placehold") ? (
                      <img
                        src={it.thumb}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <ImageOff className="w-4 h-4 text-tb-navy/40" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-tb-navy text-sm leading-tight truncate">
                      {it.name || "(no name)"}
                    </div>
                    <div className="font-hand text-tb-navy/55 text-xs italic">
                      {[it.city, it.state, it.country].filter(Boolean).join(", ") || "(no location)"}
                      {it.source ? ` · ${it.source}` : ""}
                    </div>
                    {it.state_backfilled_at && (
                      <div className="font-hand text-tb-navy/40 text-[10px] mt-0.5">
                        resolved-at {String(it.state_backfilled_at).slice(0, 19).replace("T", " ")}
                        {it.nominatim_no_state ? " · flagged no-state" : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button
                      type="button"
                      data-testid={`admin-state-backfill-edit-${it.id}`}
                      onClick={() => onEdit(it.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-tb-cream border border-tb-cream-3 text-tb-navy font-hand text-xs hover:bg-tb-cream-3/60 transition"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button
                      type="button"
                      data-testid={`admin-state-backfill-delete-${it.id}`}
                      onClick={() => setPendingDelete(it)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-tb-orange/15 border border-tb-orange/40 text-tb-orange-soft font-hand text-xs hover:bg-tb-orange/25 transition"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => { if (!v) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="admin-state-backfill-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this destination?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently remove <b>{pendingDelete?.name}</b> ({[pendingDelete?.city, pendingDelete?.state].filter(Boolean).join(", ")}) from the catalog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-tb-orange hover:brightness-110">
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}



const EMPTY_FORM = {
  name: "", type: "", city: "", state: "", country: "US",
  address: "", lat: "", lng: "",
  // (OTA30) Primary + optional secondary category — 15 canonical values.
  category: "", category_2: "",
  tags: "",
  description: "", featured_image: "",
  // (OTA32) Manual photo URLs — first entry becomes featured_image.
  photos: [""],
};

// (OTA30) 15 canonical category values shown in the admin add-form
// dropdowns. Matches `_CT_TO_PRIMARY_FILTER` on the backend
// (routers/admin.py). Order chosen to mirror the Explore filter chip
// order in `frontend/src/config/vibes.jsx`.
const CANONICAL_CATEGORIES = [
  "Hotels", "Resorts", "All-Inclusive Resorts", "Cruises", "Destinations",
  "Activities", "Camping/Campgrounds", "Unique Stays", "Lodges/Inns/B&Bs",
  "Cabins", "RV Resorts", "Theme Parks", "National Parks", "Flights",
  "Transportation",
];

// Canonical 15 (OTA35 Option B). Value === label; each option is a
// self-labelled canonical string. Same list as
// `AdminDestinationEditPage::CANONICAL_TYPES` — keep in sync with
// the backend `admin_destinations._CANONICAL_TYPES`.
const DEST_TYPES = [
  { value: "",                     label: "Select a type…", disabled: true },
  { value: "Hotels",               label: "Hotels" },
  { value: "Resorts",              label: "Resorts" },
  { value: "All-Inclusive Resorts",label: "All-Inclusive Resorts" },
  { value: "Cruises",              label: "Cruises" },
  { value: "Destinations",         label: "Destinations" },
  { value: "Activities",           label: "Activities" },
  { value: "Camping/Campgrounds",  label: "Camping/Campgrounds" },
  { value: "Unique Stays",         label: "Unique Stays" },
  { value: "Lodges/Inns/B&Bs",     label: "Lodges/Inns/B&Bs" },
  { value: "Cabins",               label: "Cabins" },
  { value: "RV Resorts",           label: "RV Resorts" },
  { value: "Theme Parks",          label: "Theme Parks" },
  { value: "National Parks",       label: "National Parks" },
  { value: "Flights",              label: "Flights" },
  { value: "Transportation",       label: "Transportation" },
];

const INPUT_CLS =
  "w-full px-3 py-2 rounded-lg bg-tb-cream-2/70 border-2 border-tb-cream-3 " +
  "font-ui text-tb-navy text-sm placeholder-tb-navy/35 " +
  "focus:outline-none focus:border-tb-coral transition";

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="font-hand text-tb-navy/75 text-sm block mb-1">
        {label}
        {required && <span className="text-tb-coral ml-1">*</span>}
        {hint && <span className="text-tb-navy/45 italic ml-1.5">· {hint}</span>}
      </span>
      {children}
    </label>
  );
}

const STATUS_META = {
  matched: { label: "Stay22 linked", cls: "bg-tb-sage/25 text-tb-navy border-tb-sage/50", Icon: CheckCircle2 },
  pending: { label: "lookup pending", cls: "bg-tb-blue/15 text-tb-blue border-tb-blue/40", Icon: Loader2, spin: true },
  unmatched: { label: "unmatched", cls: "bg-tb-orange/20 text-tb-orange-soft border-tb-orange/50", Icon: AlertCircle },
  rate_limited: { label: "rate limited", cls: "bg-tb-coral/15 text-tb-coral border-tb-coral/50", Icon: Ban },
  error: { label: "error", cls: "bg-tb-coral/20 text-tb-coral border-tb-coral/60", Icon: AlertCircle },
};

function RecentRow({ row, onChange, onDelete }) {
  const [busy, setBusy] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideValue, setOverrideValue] = useState(row.stay22_property_id || "");

  const status = row.match_status || (row.stay22_linked ? "matched" : "pending");
  const meta = STATUS_META[status] || STATUS_META.pending;
  const canRetry = ["unmatched", "rate_limited", "error"].includes(status);
  const showOverrideAffordance = canRetry || !row.stay22_linked;

  const retry = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/travelbio-destinations/${row.id}/retry-stay22`);
      toast.success(`Retry queued for '${row.name}'`);
      setTimeout(onChange, 4000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't retry");
    } finally {
      setBusy(false);
    }
  };

  const submitOverride = async () => {
    const pid = overrideValue.trim();
    if (!pid) { toast.error("Enter a Stay22 property ID"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/travelbio-destinations/${row.id}/set-stay22-id`, {
        stay22_property_id: pid,
      });
      toast.success(`Override set for '${row.name}'`);
      setOverrideOpen(false);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't override");
    } finally {
      setBusy(false);
    }
  };

  const StatusIcon = meta.Icon;

  return (
    <li className="py-1.5 px-2 rounded hover:bg-tb-cream-2/60">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-serif-it text-tb-navy truncate">
            {row.name}
            <span className="font-hand text-tb-navy/50 text-xs ml-2">
              · {[row.city, row.state, row.country].filter(Boolean).join(", ")} · {row.type}
            </span>
          </div>
        </div>
        <span
          className={
            "px-2 py-0.5 rounded-full text-xs font-hand shrink-0 flex items-center gap-1 border " +
            meta.cls
          }
          title={row.last_lookup_at ? `Last checked: ${new Date(row.last_lookup_at).toLocaleString()}` : ""}
        >
          <StatusIcon className={"w-3 h-3 " + (meta.spin ? "animate-spin" : "")} />
          {meta.label}
        </span>
        {/* (OTA32) 'Retry Stay22' per-row button removed 2026-07-11. */}
        {false && canRetry && (
          <button
            type="button"
            data-testid={ADMIN.addDestRecentRetry(row.id)}
            onClick={retry}
            disabled={busy}
            className="text-tb-blue/70 hover:text-tb-blue transition p-1 disabled:opacity-40"
            title="Retry Stay22 lookup"
            aria-label={`Retry Stay22 lookup for ${row.name}`}
          >
            <RotateCw className={"w-3.5 h-3.5 " + (busy ? "animate-spin" : "")} />
          </button>
        )}
        {/* (OTA32) Stay22 property-ID override wrench + paste dialog
            removed 2026-07-11 alongside the rest of the Stay22 UI.
            See /app/memory/INTEGRATIONS_REMOVAL.md § 2. */}
        {false && showOverrideAffordance && (
          <button
            type="button"
            data-testid={ADMIN.addDestRecentOverrideBtn(row.id)}
            onClick={() => setOverrideOpen((v) => !v)}
            className="text-tb-navy/50 hover:text-tb-navy transition p-1"
            title="Override Stay22 property ID"
            aria-label={`Override Stay22 ID for ${row.name}`}
          >
            <Wrench className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          data-testid={ADMIN.addDestRecentDelete(row.id)}
          onClick={() => onDelete(row)}
          className="text-tb-navy/40 hover:text-tb-coral transition p-1"
          aria-label={`Delete ${row.name}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {overrideOpen && (
        <div className="mt-2 ml-2 flex items-center gap-2">
          <input
            data-testid={ADMIN.addDestRecentOverrideInput(row.id)}
            className="flex-1 px-2 py-1 rounded-md bg-tb-cream-2 border border-tb-cream-3 font-mono text-xs text-tb-navy focus:outline-none focus:border-tb-coral"
            value={overrideValue}
            onChange={(e) => setOverrideValue(e.target.value)}
            placeholder="Paste Stay22 property ID (e.g. 324b1501...0000)"
          />
          <button
            type="button"
            data-testid={ADMIN.addDestRecentOverrideSubmit(row.id)}
            onClick={submitOverride}
            disabled={busy}
            className="px-2.5 py-1 rounded-md bg-tb-navy text-tb-gold-bright font-hand text-xs tb-tap disabled:opacity-60"
          >
            {busy ? "…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => setOverrideOpen(false)}
            className="text-tb-navy/45 font-hand text-xs px-1"
          >
            cancel
          </button>
        </div>
      )}
    </li>
  );
}

function AddDestinationCard() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState(null);
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadRecent = async () => {
    try {
      const { data } = await api.get("/admin/travelbio-destinations/recent", {
        params: { limit: 10 },
      });
      setRecent(data.rows || []);
    } catch {
      // silent — the section still works without recent list
    } finally {
      setRecentLoading(false);
    }
  };

  useEffect(() => { loadRecent(); }, []);

  const validate = () => {
    if (!form.name.trim()) return "Name is required";
    if (!form.city.trim()) return "City is required";
    // (OTA30) lat/lng optional now — server forward-geocodes if missing.
    if (form.lat !== "" && (Number.isNaN(Number(form.lat)) || Number(form.lat) < -90 || Number(form.lat) > 90)) {
      return "Latitude must be a number between -90 and 90";
    }
    if (form.lng !== "" && (Number.isNaN(Number(form.lng)) || Number(form.lng) < -180 || Number(form.lng) > 180)) {
      return "Longitude must be a number between -180 and 180";
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    // (OTA32) Photos — strip empties + cap at 10. First URL becomes
    // featured_image if not explicitly set.
    const cleanPhotos = (form.photos || []).map((u) => (u || "").trim()).filter(Boolean).slice(0, 10);
    const body = {
      name: form.name.trim(),
      type: form.type,
      city: form.city.trim(),
      state: form.state.trim() || null,
      country: (form.country.trim() || "US"),
      address: form.address.trim() || null,
      lat: form.lat === "" ? null : Number(form.lat),
      lng: form.lng === "" ? null : Number(form.lng),
      category: form.category || null,
      category_2: form.category_2 || null,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      description: form.description,
      featured_image: form.featured_image.trim() || null,
      images: cleanPhotos,
    };
    try {
      const { data } = await api.post("/admin/travelbio-destinations/create", body);
      toast.success(
        `'${data.name}' added! Booking.com link auto-populated${data.needs_geocode ? " · geocode pending" : ""}.`
      );
      setLastSubmitted(form);
      setForm(EMPTY_FORM);
      // Refresh recent list — the row is added at status "pending" for a
      // beat, then flips to "linked" once the background task lands.
      loadRecent();
      setTimeout(loadRecent, 4000);
    } catch (e2) {
      const msg = formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't add destination";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const duplicate = () => {
    if (!lastSubmitted) return;
    setForm({ ...lastSubmitted, name: "", lat: "", lng: "" });
    toast("Form pre-filled — clear the name + coordinates, then submit again.");
  };

  const deleteRecent = async (row) => {
    if (!window.confirm(`Delete '${row.name}' (${row.city})? This removes the card from the catalog.`)) return;
    try {
      await api.delete(`/admin/travelbio-destinations/${row.id}`);
      setRecent((r) => r.filter((x) => x.id !== row.id));
      toast.success(`Deleted '${row.name}'`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  return (
    <section
      data-testid={ADMIN.addDestCard}
      className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-full bg-tb-coral/15 border border-tb-coral/40 flex items-center justify-center">
          <Plus className="w-5 h-5 text-tb-coral" />
        </div>
        <div>
          <h2 className="font-serif-it text-tb-navy text-2xl leading-tight">Add New Destination</h2>
          <p className="font-hand text-tb-navy/60 text-sm italic">
            Manually add a card to the catalog — Booking.com CJ link is auto-generated on save.
            Category dropdowns + photo URLs (up to 10, first becomes the featured image).
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Name" required>
          <input
            data-testid={ADMIN.addDestNameInput}
            className={INPUT_CLS}
            value={form.name}
            onChange={set("name")}
            placeholder="Great Wolf Lodge Poconos"
          />
        </Field>

        <Field label="Type" required>
          <select
            data-testid={ADMIN.addDestTypeSelect}
            className={INPUT_CLS}
            value={form.type}
            onChange={set("type")}
          >
            {DEST_TYPES.map((t) => (
              <option key={t.value} value={t.value} disabled={t.disabled}>{t.label}</option>
            ))}
          </select>
        </Field>

        <Field label="City" required>
          <input
            data-testid={ADMIN.addDestCityInput}
            className={INPUT_CLS}
            value={form.city}
            onChange={set("city")}
            placeholder="Scotrun"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="State" hint="2-letter">
            <input
              data-testid={ADMIN.addDestStateInput}
              className={INPUT_CLS}
              value={form.state}
              onChange={set("state")}
              maxLength={2}
              placeholder="PA"
            />
          </Field>
          <Field label="Country" hint="ISO">
            <input
              data-testid={ADMIN.addDestCountryInput}
              className={INPUT_CLS}
              value={form.country}
              onChange={set("country")}
              maxLength={2}
              placeholder="US"
            />
          </Field>
        </div>

        <Field label="Address">
          <input
            data-testid={ADMIN.addDestAddressInput}
            className={INPUT_CLS}
            value={form.address}
            onChange={set("address")}
            placeholder="1 Great Wolf Dr"
          />
        </Field>

        <div className="md:col-span-2 rounded-lg bg-tb-blue/10 border border-tb-blue/30 px-3 py-2 flex items-start gap-2">
          <MapPin className="w-4 h-4 text-tb-blue flex-shrink-0 mt-0.5" />
          <p className="font-hand text-tb-navy/70 text-xs leading-snug">
            <b className="text-tb-navy">Coordinates:</b> get them from Google Maps — right-click any
            location and click the coordinates that appear to copy them. Or use{" "}
            <a
              href="https://www.latlong.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-tb-coral underline"
            >
              latlong.net
            </a>.
          </p>
        </div>

        <Field label="Latitude" required hint="e.g. 41.0762">
          <input
            data-testid={ADMIN.addDestLatInput}
            type="number"
            step="0.0001"
            className={INPUT_CLS}
            value={form.lat}
            onChange={set("lat")}
            placeholder="41.0762"
          />
        </Field>

        <Field label="Longitude" required hint="e.g. -75.3193">
          <input
            data-testid={ADMIN.addDestLngInput}
            type="number"
            step="0.0001"
            className={INPUT_CLS}
            value={form.lng}
            onChange={set("lng")}
            placeholder="-75.3193"
          />
        </Field>

        <Field label="Primary Category" required hint="15 canonical values">
          <select
            data-testid="admin-add-dest-category-primary"
            className={INPUT_CLS}
            value={form.category}
            onChange={set("category")}
          >
            <option value="">— select —</option>
            {CANONICAL_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </Field>

        <Field label="Secondary Category" hint="optional · shown in a second filter chip">
          <select
            data-testid="admin-add-dest-category-secondary"
            className={INPUT_CLS}
            value={form.category_2}
            onChange={set("category_2")}
          >
            <option value="">— None —</option>
            {CANONICAL_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </Field>

        <Field label="Tags" hint="comma-separated">
          <input
            data-testid={ADMIN.addDestTagsInput}
            className={INPUT_CLS}
            value={form.tags}
            onChange={set("tags")}
            placeholder="waterpark, family, indoor"
          />
        </Field>

        <div className="md:col-span-2">
          <Field label="Description">
            <textarea
              data-testid={ADMIN.addDestDescriptionInput}
              className={INPUT_CLS + " min-h-[110px] resize-y"}
              rows={5}
              value={form.description}
              onChange={set("description")}
              placeholder="Optional — write your own copy, or leave blank."
            />
          </Field>
        </div>

        {/* (OTA32) Photo URLs — repeatable list, up to 10. First non-empty
            URL becomes featured_image automatically. */}
        <div className="md:col-span-2">
          <Field label="Photo URLs" hint="up to 10 · first becomes the featured image">
            <div className="space-y-2" data-testid="admin-add-dest-photos-list">
              {form.photos.map((url, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    data-testid={`admin-add-dest-photo-${i + 1}`}
                    className={INPUT_CLS + " flex-1"}
                    value={url}
                    placeholder={`Photo URL ${i + 1} · https://…`}
                    onChange={(e) => setForm((f) => {
                      const next = [...f.photos];
                      next[i] = e.target.value;
                      return { ...f, photos: next };
                    })}
                  />
                  {form.photos.length > 1 && (
                    <button
                      type="button"
                      data-testid={`admin-add-dest-photo-remove-${i + 1}`}
                      onClick={() => setForm((f) => ({
                        ...f, photos: f.photos.filter((_, j) => j !== i),
                      }))}
                      className="px-3 py-2 rounded-lg bg-tb-cream-2 border-2 border-tb-cream-3 text-tb-navy font-hand text-sm hover:bg-tb-coral/10 hover:border-tb-coral/30"
                      title="Remove this photo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {form.photos.length < 10 && (
                <button
                  type="button"
                  data-testid="admin-add-dest-photo-add"
                  onClick={() => setForm((f) => ({ ...f, photos: [...f.photos, ""] }))}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 text-tb-navy font-hand text-sm hover:brightness-105"
                >
                  <Plus className="w-3.5 h-3.5" /> Add another photo
                </button>
              )}
            </div>
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Featured image URL" hint="optional — first photo above is used if blank">
            <input
              data-testid={ADMIN.addDestImageInput}
              className={INPUT_CLS}
              value={form.featured_image}
              onChange={set("featured_image")}
              placeholder="https://..."
            />
          </Field>
        </div>

        <div className="md:col-span-2 flex flex-wrap gap-2 pt-1">
          <button
            data-testid={ADMIN.addDestSubmitBtn}
            type="submit"
            disabled={submitting}
            className="px-5 py-2 rounded-full bg-tb-coral text-white font-hand text-base tb-tap disabled:opacity-60 flex items-center gap-1.5 shadow hover:brightness-110 transition"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {submitting ? "Creating…" : "Create Destination"}
          </button>
          {lastSubmitted && (
            <button
              data-testid={ADMIN.addDestDuplicateBtn}
              type="button"
              onClick={duplicate}
              className="px-4 py-2 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 text-tb-navy font-hand text-base tb-tap flex items-center gap-1.5 hover:bg-tb-cream-3/60 transition"
            >
              <Copy className="w-4 h-4" /> Duplicate last
            </button>
          )}
        </div>
      </form>

      {/* Recently-added list */}
      <div data-testid={ADMIN.addDestRecentList} className="mt-6 border-t border-tb-cream-3 pt-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif-it text-tb-navy text-lg">Recently added · manual</h3>
          <button
            type="button"
            onClick={loadRecent}
            className="text-tb-navy/60 hover:text-tb-coral transition"
            aria-label="refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        {recentLoading ? (
          <p className="font-hand text-tb-navy/50 italic text-sm">loading…</p>
        ) : recent.length === 0 ? (
          <p className="font-hand text-tb-navy/50 italic text-sm">
            no manually-added destinations yet — create one above ✦
          </p>
        ) : (
          <ul className="space-y-1.5">
            {recent.map((r) => (
              <RecentRow
                key={r.id}
                row={r}
                onChange={loadRecent}
                onDelete={deleteRecent}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function ReviewQueue() {
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ open: 0, dismissed: 0, actioned: 0 });
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const load = async (s = status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: { status: s } });
      setReports(data.reports || []);
      setCounts(data.counts || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(status); }, [status]); // eslint-disable-line
  const dismiss = async (r) => {
    try {
      await api.post(`/admin/reports/${r.id}/dismiss`);
      setReports((p) => p.filter((x) => x.id !== r.id));
      setCounts((c) => ({ ...c, open: Math.max(0, c.open - 1), dismissed: c.dismissed + 1 }));
      toast.success("Flag dismissed");
    } catch (e) { toast.error("Couldn't dismiss"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete the ${r.target_type}? The user will lose this content.`)) return;
    try {
      await api.post(`/admin/reports/${r.id}/remove`);
      setReports((p) => p.filter((x) => x.id !== r.id));
      setCounts((c) => ({ ...c, open: Math.max(0, c.open - 1), actioned: c.actioned + 1 }));
      toast.success("Content removed");
    } catch (e) { toast.error("Couldn't remove"); }
  };
  return (
    <section data-testid="admin-review-queue" className="space-y-3 mt-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-tb-navy text-2xl">Review queue</h2>
        <div className="flex gap-1.5 text-sm">
          {["open", "dismissed", "actioned"].map((s) => (
            <button
              key={s}
              data-testid={`admin-queue-tab-${s}`}
              onClick={() => setStatus(s)}
              className={
                "px-3 py-1 rounded-full font-hand border-2 tb-tap " +
                (status === s
                  ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                  : "bg-tb-cream-2 text-tb-navy/70 border-tb-cream-3")
              }
            >
              {s} <span className="text-xs">({counts[s] || 0})</span>
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <p className="font-hand text-tb-navy/55 italic text-sm">loading…</p>
      ) : reports.length === 0 ? (
        <div className="text-center py-6 rounded-2xl border-2 border-dashed border-tb-cream-3 bg-tb-cream-2/40">
          <p className="font-hand text-tb-navy/55 italic text-sm">
            no flags in this queue ✦
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => (
            <article
              key={r.id}
              data-testid={`admin-report-${r.id}`}
              className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-3"
            >
              <header className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-serif-it text-tb-navy text-base truncate">
                    {r.target_type}
                  </span>
                  <span className="text-xs font-hand text-tb-navy/55">{timeAgo(r.created_at)}</span>
                </div>
                {status === "open" && (
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      data-testid={`admin-dismiss-${r.id}`}
                      onClick={() => dismiss(r)}
                      className="px-3 py-1 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 text-tb-navy font-hand text-xs tb-tap"
                    >
                      dismiss
                    </button>
                    <button
                      data-testid={`admin-remove-${r.id}`}
                      onClick={() => remove(r)}
                      className="px-3 py-1 rounded-full bg-tb-coral text-white font-hand text-xs tb-tap hover:brightness-110"
                    >
                      remove
                    </button>
                  </div>
                )}
              </header>
              {r.target_user && (
                <p className="font-hand text-tb-navy/70 text-xs">
                  by <b>{r.target_user.name}</b> · @{r.target_user.username || r.target_user.email?.split("@")[0]}
                </p>
              )}
              {r.target?.text && (
                <p className="font-ui text-sm text-tb-navy/85 mt-1.5 p-2 rounded bg-tb-cream-2/70 whitespace-pre-wrap line-clamp-4">
                  "{r.target.text}"
                </p>
              )}
              {r.reason && (
                <p className="font-hand text-tb-coral/90 italic text-xs mt-1.5">
                  reason: {r.reason}
                </p>
              )}
              <p className="font-hand text-tb-navy/45 text-xs mt-1.5">
                flagged by {r.reporter_name || r.reporter_email}
              </p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}


// -----------------------------------------------------------------
// Import Destinations CSV — admin can trigger the same
// /api/admin/travelbio-destinations/import-csv endpoint used by the
// nightly append job. Paste a CSV URL, tick "Dry run" to preview, hit
// "Import". Response shows inline: inserted, skipped duplicates,
// geocode failures, plus a timestamp. Last result persists in local
// state until the next import.
// -----------------------------------------------------------------
function ImportDestinationsCsv() {
  const [mode, setMode] = useState("url");  // "url" | "file"
  const [csvUrl, setCsvUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  // (OTA31) When checked, existing TravelBioIDs get their fields
  // overwritten from the CSV instead of being silently skipped.
  const [updateExisting, setUpdateExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [ranAt, setRanAt] = useState(null);
  const [pollUntil, setPollUntil] = useState(0);   // Date.now() ms deadline for backfill polling
  // Pre-flight modal state — populated when a file upload flags rows.
  const [preflight, setPreflight] = useState(null);   // { report, row_count }
  const [preflightOpen, setPreflightOpen] = useState(false);

  const doCommit = async () => {
    // Real commit — the modal-close path routes here after the admin
    // picks a strategy (import-all). "valid_only" and "cancel" don't
    // trigger a commit yet (see modal); today the CSV endpoint has no
    // per-index skip so we always commit whole-file on Import-all and
    // let the Repair Bad Locations job clean up afterward.
    setBusy(true);
    setResult(null);
    try {
      let data;
      if (mode === "url") {
        const url = csvUrl.trim();
        if (!url) { toast.error("Paste a CSV URL first."); setBusy(false); return; }
        ({ data } = await api.post("/admin/travelbio-destinations/import-csv",
          { csv_url: url, dry_run: dryRun, update_existing: updateExisting }));
      } else {
        if (!selectedFile) { toast.error("Choose a file first."); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", selectedFile);
        ({ data } = await api.post(
          `/admin/travelbio-destinations/import-file?dry_run=${dryRun}&update_existing=${updateExisting}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } },
        ));
      }
      setResult({ ok: true, data });
      setRanAt(new Date());
      const label = dryRun ? "dry-run" : "import";
      toast.success(
        `${label} complete — ${data.inserted || data.would_insert || 0} inserted, ${data.skipped_duplicates || data.would_skip_duplicates || 0} dupes`,
      );
      if (!dryRun && (data.inserted || 0) > 0) {
        setPollUntil(Date.now() + 45 * 60 * 1000);
      }
    } catch (e) {
      setResult({ ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) || e.message });
      setRanAt(new Date());
      toast.error("Import failed — see result panel.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    // For non-dry-run FILE uploads, run pre-flight first. If flagged
    // rows > 0, show the modal to let the admin decide. URL imports
    // and dry-runs skip pre-flight — the file bytes aren't in-hand for
    // URL mode, and dry-runs are already a preview.
    if (!dryRun && mode === "file" && selectedFile) {
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", selectedFile);
        const { data } = await api.post(
          "/admin/travelbio-destinations/import-preflight",
          fd, { headers: { "Content-Type": "multipart/form-data" } },
        );
        const flagged = data?.report?.flagged || 0;
        if (flagged > 0) {
          setPreflight(data);
          setPreflightOpen(true);
          setBusy(false);
          return;
        }
      } catch (e) {
        // Pre-flight failure is non-fatal — fall through to commit.
        // eslint-disable-next-line no-console
        console.warn("preflight skipped:", e);
      } finally {
        setBusy(false);
      }
    }
    await doCommit();
  };

  const runRepairAfterImport = async () => {
    try {
      await api.post("/admin/destinations/repair-bad-locations?dry_run=false");
      toast.info("Repair Bad Locations job started — check the Maintenance panel for results.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start repair");
    }
  };

  return (
    <section
      className="tb-paper-soft rounded-2xl p-5 space-y-4"
      data-testid="admin-import-csv"
    >
      <div>
        <h2 className="font-serif-it text-tb-navy text-2xl leading-tight">
          Import destinations
        </h2>
        <p className="font-hand text-tb-blue/70 text-sm italic mt-1">
          Paste a CSV URL or upload a CSV / Excel file. Existing
          TravelBioIDs will be skipped — only new rows are added. Tick
          <b> Update existing rows</b> to overwrite matching TravelBioIDs
          instead of skipping. Booking.com CJ URLs are auto-populated on
          insert (no external API calls). Photos come from the CSV
          <code> photo_1 … photo_10</code> or <code>photos</code>
          comma-list columns.
        </p>
      </div>

      {/* Mode toggle */}
      <div
        role="tablist"
        aria-label="Import source"
        className="inline-flex rounded-full bg-tb-cream border-2 border-tb-cream-3 p-1"
      >
        {[
          { key: "url",  label: "From URL"    },
          { key: "file", label: "Upload File" },
        ].map((m) => (
          <button
            key={m.key}
            type="button"
            role="tab"
            aria-selected={mode === m.key}
            data-testid={`admin-csv-mode-${m.key}`}
            onClick={() => { setMode(m.key); setResult(null); }}
            className={
              mode === m.key
                ? "px-4 py-1.5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm"
                : "px-4 py-1.5 rounded-full text-tb-navy/60 font-hand text-sm hover:text-tb-navy"
            }
          >{m.label}</button>
        ))}
      </div>

      {/* Template download links — same for both modes. Cookies carry
          admin auth, so a raw <a> href works. */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-hand text-tb-navy/60 italic">
          Need the shape? Download a sample:
        </span>
        <a
          href={`${API_BASE}/admin/travelbio-destinations/import-template?format=csv`}
          data-testid="admin-csv-template-download-csv"
          className="font-hand text-tb-navy underline decoration-tb-gold underline-offset-2 hover:decoration-2"
        >CSV template</a>
        <span className="font-hand text-tb-navy/40">·</span>
        <a
          href={`${API_BASE}/admin/travelbio-destinations/import-template?format=xlsx`}
          data-testid="admin-csv-template-download-xlsx"
          className="font-hand text-tb-navy underline decoration-tb-gold underline-offset-2 hover:decoration-2"
        >Excel template</a>
        <span className="font-hand text-tb-navy/50 italic text-xs w-full">
          Header row + one placeholder row. XLSX version marks required columns
          in navy and ships an <em>Instructions</em> sheet with the column key.
        </span>
      </div>

      <div className="space-y-3">
        {mode === "url" ? (
          <label className="block">
            <span className="font-hand text-tb-navy/70 text-xs uppercase tracking-wider">
              CSV URL
            </span>
            <input
              type="url"
              data-testid="admin-csv-url-input"
              value={csvUrl}
              onChange={(e) => setCsvUrl(e.target.value)}
              placeholder="https://customer-assets.emergentagent.com/…/travelbio_daily_append_YYYY-MM-DD_NNN.csv"
              className="mt-1 w-full px-3 py-2 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm text-tb-navy"
            />
          </label>
        ) : (
          <label className="block">
            <span className="font-hand text-tb-navy/70 text-xs uppercase tracking-wider">
              File
            </span>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              data-testid="admin-csv-file-input"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-tb-navy file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-tb-navy file:text-tb-gold-bright file:font-hand file:cursor-pointer hover:file:bg-tb-navy/90"
            />
            <span className="font-hand text-tb-navy/50 text-xs italic block mt-1">
              Supports .csv, .xlsx, .xls up to 10 MB.
              {selectedFile && (
                <span className="ml-2 text-tb-navy/80 not-italic">
                  selected: <b>{selectedFile.name}</b> ({Math.round(selectedFile.size / 1024)} KB)
                </span>
              )}
            </span>
          </label>
        )}

        <label
          className="inline-flex items-center gap-2 cursor-pointer select-none"
          data-testid="admin-csv-dryrun-label"
        >
          <input
            type="checkbox"
            data-testid="admin-csv-dryrun-checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            className="accent-tb-gold w-4 h-4"
          />
          <span className="font-hand text-tb-navy text-sm">
            Dry run (preview counts, no writes)
          </span>
        </label>

        <label
          className="inline-flex items-center gap-2 cursor-pointer select-none"
          data-testid="admin-csv-update-existing-label"
          title="When ticked, existing TravelBioIDs get their fields overwritten from the CSV instead of skipped."
        >
          <input
            type="checkbox"
            data-testid="admin-csv-update-existing-checkbox"
            checked={updateExisting}
            onChange={(e) => setUpdateExisting(e.target.checked)}
            className="accent-tb-gold w-4 h-4"
          />
          <span className="font-hand text-tb-navy text-sm">
            Update existing rows on TravelBioID match
          </span>
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="admin-csv-import-btn"
            onClick={submit}
            disabled={busy || (mode === "url" ? !csvUrl.trim() : !selectedFile)}
            className="px-5 py-2 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap hover:bg-tb-navy/90 disabled:opacity-50"
          >
            {busy ? "Importing…" : dryRun ? "Preview (dry run)" : "Import"}
          </button>
          {ranAt && (
            <span className="font-hand text-tb-navy/50 text-xs italic">
              last run: {ranAt.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {result && (
        <div
          data-testid="admin-csv-import-result"
          className={
            result.ok
              ? "rounded-lg border border-tb-sage/40 bg-tb-sage/10 p-3 space-y-1"
              : "rounded-lg border border-tb-orange/50 bg-tb-orange/20 p-3"
          }
        >
          {result.ok ? (
            <>
              <div className="font-hand text-tb-navy text-sm">
                {dryRun ? "Dry-run summary" : "Import complete"}
              </div>
              <ul className="font-ui text-tb-navy/85 text-xs space-y-0.5">
                <li>inserted: <b>{result.data.inserted ?? result.data.would_insert ?? 0}</b></li>
                <li>skipped duplicates: <b>{result.data.skipped_duplicates ?? result.data.would_skip_duplicates ?? 0}</b></li>
                <li>skipped cruises: <b>{result.data.skipped_cruises ?? result.data.would_skip_cruises ?? 0}</b></li>
                <li>geocoded: <b>{result.data.geocoded ?? 0}</b></li>
                <li>geocode failures: <b>{result.data.geocode_failures ?? 0}</b></li>
                <li>auto-reclassified: <b>{result.data.auto_reclassified_count ?? 0}</b></li>
              </ul>
            </>
          ) : (
            <div className="font-ui text-tb-orange-soft text-xs">
              {String(result.error)}
            </div>
          )}
        </div>
      )}

      {/* (2026-07-12) Stay22 photo-backfill progress card removed —
          Stay22 dropped as a provider; the widget was vestigial.
          BackfillProgressCard function retained (see @deprecated tag)
          in case a future backfill wants to reuse the visual. */}

      <AlertDialog open={preflightOpen} onOpenChange={(v) => { if (!v) setPreflightOpen(false); }}>
        <AlertDialogContent
          data-testid="admin-csv-preflight-modal"
          className="max-w-2xl"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-it text-tb-navy text-2xl">
              Pre-flight found <span className="text-tb-orange-soft">{preflight?.report?.flagged || 0}</span> row{(preflight?.report?.flagged || 0) === 1 ? "" : "s"} to review
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 font-hand text-tb-navy text-sm">
                <div className="font-ui text-tb-navy/80 text-xs">
                  {preflight?.summary}
                </div>
                {(preflight?.report?.invalid_state_rows?.length || 0) > 0 && (
                  <div>
                    <div className="font-serif text-tb-navy text-base">
                      Invalid state values ({preflight.report.invalid_state_rows.length})
                    </div>
                    <ul className="font-ui text-tb-navy/85 text-xs max-h-32 overflow-y-auto pl-4 list-disc">
                      {preflight.report.invalid_state_rows.slice(0, 20).map((r) => (
                        <li key={r.row_index}>
                          row {r.row_index + 2}: <b>{r.name || "(no name)"}</b> · state=<code>{r.state}</code> for <code>{r.country}</code>
                        </li>
                      ))}
                      {preflight.report.invalid_state_rows.length > 20 && (
                        <li className="italic text-tb-navy/60">…and {preflight.report.invalid_state_rows.length - 20} more</li>
                      )}
                    </ul>
                  </div>
                )}
                {(preflight?.report?.empty_country_rows?.length || 0) > 0 && (
                  <div>
                    <div className="font-serif text-tb-navy text-base">
                      Empty country ({preflight.report.empty_country_rows.length})
                    </div>
                    <ul className="font-ui text-tb-navy/85 text-xs max-h-24 overflow-y-auto pl-4 list-disc">
                      {preflight.report.empty_country_rows.slice(0, 20).map((r) => (
                        <li key={r.row_index}>row {r.row_index + 2}: <b>{r.name || "(no name)"}</b> · city={r.city || "—"}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(preflight?.report?.template_city_rows?.length || 0) > 0 && (
                  <div>
                    <div className="font-serif text-tb-navy text-base">
                      Template-city suspicion ({preflight.report.template_city_rows.length})
                    </div>
                    <div className="font-ui text-tb-navy/85 text-xs">
                      One city appears in ≥20 rows AND matches a known template default (e.g. "San Antonio"). Likely a copy-paste artefact.
                    </div>
                  </div>
                )}
                {(preflight?.report?.unknown_category_rows?.length || 0) > 0 && (
                  <div data-testid="admin-csv-preflight-unknown-category">
                    <div className="font-serif text-tb-navy text-base">
                      Unknown category ({preflight.report.unknown_category_rows.length})
                    </div>
                    <div className="font-ui text-tb-navy/85 text-xs mb-1">
                      Row <code>Type</code> doesn't match a canonical category
                      (OTA35 policy is case + trailing-<code>s</code> tolerant only —
                      no semantic aliasing). These rows will be skipped on import.
                    </div>
                    <ul className="font-hand text-tb-navy/80 text-xs italic space-y-0.5 pl-4 list-disc">
                      {preflight.report.unknown_category_rows.slice(0, 20).map((r) => (
                        <li
                          key={`unk-cat-${r.row_index}`}
                          data-testid={`admin-csv-preflight-unknown-category-row-${r.row_index}`}
                        >
                          row {r.row_index}: {r.name || "(no name)"}
                          {r.id ? <> · <b className="not-italic">{r.id}</b></> : null}
                          {" · type="}
                          <b className="not-italic">{r.provided_type}</b>
                        </li>
                      ))}
                      {preflight.report.unknown_category_rows.length > 20 && (
                        <li className="italic text-tb-navy/60">…and {preflight.report.unknown_category_rows.length - 20} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-3">
            <AlertDialogCancel
              data-testid="admin-csv-preflight-cancel"
              className="bg-tb-cream-2 border-2 border-tb-cream-3 text-tb-navy font-hand"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-csv-preflight-import-all-repair"
              onClick={async () => {
                setPreflightOpen(false);
                await doCommit();
                await runRepairAfterImport();
              }}
              className="bg-tb-orange text-white hover:brightness-110 font-hand"
            >
              Import all + auto-repair
            </AlertDialogAction>
            <AlertDialogAction
              data-testid="admin-csv-preflight-import-anyway"
              onClick={async () => {
                setPreflightOpen(false);
                await doCommit();
              }}
              className="bg-tb-navy text-tb-gold-bright font-hand"
            >
              Import anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}


// -----------------------------------------------------------------
// Backfill progress card — appears after a real (non-dry-run) CSV
// import. Polls `/admin/travelbio-destinations/backfill-status` every
// 15 seconds and shows how many rows still owe a Stay22 property_id
// (and therefore a Stay22 photo). Rolling 3-poll window drives ETA.
// Silent if there's nothing to backfill.
// -----------------------------------------------------------------
function BackfillProgressCard({ pollUntil, onDone }) {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);   // [{t, awaiting}]
  const [completed, setCompleted] = useState(false);

  const active = pollUntil > 0 && Date.now() < pollUntil && !completed;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/admin/travelbio-destinations/backfill-status");
        if (cancelled) return;
        setStatus(data);
        setHistory((h) => {
          const next = [...h, { t: Date.now(), awaiting: data.awaiting_lookup }];
          return next.slice(-3);
        });
        if (data.awaiting_lookup === 0) {
          setCompleted(true);
          onDone && onDone();
        }
      } catch {
        // Silent — transient errors during polling shouldn't nag the admin.
      }
    };

    tick();  // fire immediately, then interval
    const id = setInterval(tick, 15_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [active, onDone]);

  // Nothing to show if we're not actively polling AND we didn't just
  // finish a run.
  if (!status) return null;
  if (!active && !completed) return null;

  const total = status.total || 0;
  const unfindable = status.unfindable_stay22_property_id || 0;
  const resolvable = Math.max(total - unfindable, 0);
  const done = status.with_stay22_property_id || 0;
  const pct = resolvable > 0 ? Math.min(100, Math.round((done / resolvable) * 100)) : 100;

  // ETA from rolling window: average delta per interval (15s), extrapolate.
  let etaLabel = null;
  if (history.length >= 2 && status.awaiting_lookup > 0) {
    const first = history[0];
    const last = history[history.length - 1];
    const elapsedSec = Math.max((last.t - first.t) / 1000, 1);
    const delta = Math.max(first.awaiting - last.awaiting, 0);
    if (delta > 0) {
      const perSec = delta / elapsedSec;
      const remainingSec = Math.round(status.awaiting_lookup / perSec);
      const mins = Math.max(1, Math.round(remainingSec / 60));
      etaLabel = `~${mins} min`;
    } else {
      etaLabel = "estimating…";
    }
  }

  return (
    <div
      data-testid="admin-backfill-progress"
      className="rounded-lg border border-tb-gold/50 bg-tb-cream/60 p-3 space-y-2"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-hand text-tb-navy text-sm">
            {completed
              ? "✓ Backfill complete. All resolvable cards have Stay22 photos."
              : `Stay22 photo backfill: ${done} of ${resolvable} complete`}
          </div>
          <div className="font-hand text-tb-navy/60 text-xs italic">
            {completed
              ? `${unfindable} card${unfindable === 1 ? "" : "s"} marked unfindable and skipped.`
              : `${status.awaiting_lookup} still awaiting Stay22 lookup${
                  unfindable ? ` · ${unfindable} unfindable (skipped)` : ""
                }${etaLabel ? ` · ETA ${etaLabel}` : ""}`}
          </div>
        </div>
        <div
          className="font-serif text-tb-navy text-xl"
          data-testid="admin-backfill-progress-pct"
        >{pct}%</div>
      </div>
      <div
        className="w-full h-2 rounded-full bg-tb-cream-3 overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-tb-gold transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}


// -----------------------------------------------------------------
// RepairBadLocationsRowsDrawer — mirror of StateBackfillRowsDrawer
// with buckets (repaired / no_change / failed) and a per-row diff
// tooltip showing the before/after location triple. The drawer talks
// to /admin/destinations/repair-bad-locations/{job_id}/rows.
// -----------------------------------------------------------------
const REPAIR_FAILURE_LABELS = {
  missing_latlng_no_forward_match: "Missing coordinates, name lookup failed",
  reverse_returned_none:           "Coordinates unresolved",
  reverse_and_forward_both_none:   "All lookups failed",
  nominatim_error:                 "Nominatim error / rate-limited",
};
const REPAIR_FAILURE_TONES = {
  missing_latlng_no_forward_match: "text-tb-orange-soft",
  reverse_returned_none:           "text-tb-blue",
  reverse_and_forward_both_none:   "text-tb-coral",
  nominatim_error:                 "text-tb-navy",
};

function RepairBadLocationsRowsDrawer({ open, jobId, counts, onClose, onEdit, refreshKey }) {
  const [tab, setTab] = useState("repaired");
  const [items, setItems] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);

  const bucketCount = (b) => (counts?.[b] || []).length;

  useEffect(() => {
    if (!open || !jobId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/admin/destinations/repair-bad-locations/${encodeURIComponent(jobId)}/rows`,
          { params: { bucket: tab } },
        );
        if (cancelled) return;
        setItems(data.items || []);
        setTruncated(!!data.truncated);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load rows");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, jobId, tab, refreshKey]);

  const copyIds = async () => {
    if (items.length === 0) return;
    const ids = items.map((it) => it.travelbio_id || it.id).filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(ids);
      toast.success(`Copied ${items.length} id${items.length === 1 ? "" : "s"} to clipboard`);
    } catch {
      toast.error("Clipboard write blocked");
    }
  };

  const tabDefs = [
    { key: "repaired",  label: "Repaired" },
    { key: "no_change", label: "No change" },
    { key: "failed",    label: "Failed" },
  ];

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        data-testid="admin-repair-locations-drawer"
        className="w-full sm:max-w-2xl overflow-y-auto bg-tb-cream border-l-2 border-tb-cream-3"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <SheetHeader className="text-left mb-3">
          <SheetTitle className="font-serif-it text-tb-navy text-2xl">
            Repair Bad Locations · results
          </SheetTitle>
          <SheetDescription className="font-hand text-tb-navy/60 italic">
            job {jobId || "(none)"} · rows touched by the repair job
          </SheetDescription>
        </SheetHeader>

        <div
          role="tablist"
          aria-label="Repair bucket"
          className="inline-flex rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 p-1 mb-3"
        >
          {tabDefs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              data-testid={`admin-repair-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={
                tab === t.key
                  ? "px-3 py-1 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm"
                  : "px-3 py-1 rounded-full text-tb-navy/60 font-hand text-sm hover:text-tb-navy"
              }
            >
              {t.label} ({bucketCount(t.key)})
            </button>
          ))}
        </div>

        <div className="mb-2 flex flex-wrap items-center gap-3 font-hand text-xs">
          <button
            type="button"
            data-testid="admin-repair-copy-ids"
            onClick={copyIds}
            disabled={items.length === 0}
            className="text-tb-navy underline decoration-tb-gold underline-offset-2 hover:decoration-2 disabled:opacity-40 disabled:no-underline"
          >
            Copy {items.length} id{items.length === 1 ? "" : "s"}
          </button>
          {truncated && (
            <span className="text-tb-orange-soft italic">
              (bucket capped at 1000 — showing the first 1000)
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-6 font-hand text-tb-navy/60 italic">
            <Loader2 className="w-4 h-4 animate-spin" /> loading…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl bg-tb-cream-2/70 border border-tb-cream-3 p-6 text-center font-hand text-tb-navy/60 italic">
            No cards in this bucket.
          </div>
        ) : (
          <ul
            data-testid="admin-repair-list"
            className="divide-y divide-tb-cream-3/60 rounded-xl bg-tb-cream-2/50 border border-tb-cream-3 overflow-hidden"
          >
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`admin-repair-row-${it.id}`}
                className="flex items-start gap-3 p-3"
              >
                <div className="w-12 h-12 rounded-lg bg-tb-cream-3 flex-shrink-0 overflow-hidden flex items-center justify-center">
                  {it.thumb && !String(it.thumb).toLowerCase().includes("placehold") ? (
                    <img src={it.thumb} alt="" loading="lazy"
                         className="w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <ImageOff className="w-4 h-4 text-tb-navy/40" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-tb-navy text-sm leading-tight truncate">
                    {it.name || "(no name)"}
                  </div>
                  <div className="font-hand text-tb-navy/55 text-xs italic">
                    {[it.city, it.state, it.country].filter(Boolean).join(", ") || "(no location)"}
                  </div>
                  {it.diff?.before && it.diff?.after && (
                    <div className="mt-1 font-hand text-tb-navy/50 text-[11px]">
                      <span className="text-tb-orange-soft line-through">
                        {[it.diff.before.city, it.diff.before.state, it.diff.before.country].filter(Boolean).join(", ")}
                      </span>
                      {" → "}
                      <span className="text-tb-navy">
                        {[it.diff.after.city, it.diff.after.state, it.diff.after.country].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}
                  {tab === "failed" && it.location_repair_failed_reason && (
                    <div
                      data-testid={`admin-repair-reason-${it.id}`}
                      className={`mt-1 font-hand text-[11px] ${REPAIR_FAILURE_TONES[it.location_repair_failed_reason] || "text-tb-navy/60"}`}
                    >
                      <span className="uppercase tracking-wide text-[10px] mr-1 opacity-60">reason</span>
                      {REPAIR_FAILURE_LABELS[it.location_repair_failed_reason] || it.location_repair_failed_reason}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`admin-repair-edit-${it.id}`}
                  onClick={() => onEdit(it.id)}
                  className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded bg-tb-cream border border-tb-cream-3 text-tb-navy font-hand text-xs hover:bg-tb-cream-3/60 transition"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              </li>
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}


// -----------------------------------------------------------------
// (HotelbedsDailyPull component removed 2026-07-11 — see /app/memory/HOTELBEDS_REMOVAL.md.)


function BookingComCjPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileEmails, setReconcileEmails] = useState({});
  const [forceRebuildOpen, setForceRebuildOpen] = useState(false);
  // Foreground-tracked backfill: `job` mirrors the /status response
  // shape so the progress-bar UI is a pure projection. Persisted in
  // localStorage so a page refresh mid-run reattaches to the same
  // in-flight job instead of showing an idle button.
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/booking-com/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load Booking.com CJ status");
    }
  };
  useEffect(() => { load(); }, []);

  // ---- Backfill job polling (shared between /start and refresh-reattach) ----
  const clearPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const finalizeJob = (finalJob) => {
    clearPolling();
    try { localStorage.removeItem("bc_backfill_job_id"); } catch {}
    const s = finalJob?.summary;
    if (finalJob?.state === "done" && s) {
      toast.success(
        `Backfill done · scanned ${s.scanned || 0}, URLs set ${s.url_set || 0}, no-url ${s.no_url || 0} · ${s.duration_seconds || 0}s`,
      );
    } else if (finalJob?.state === "error") {
      const err = (finalJob.errors && finalJob.errors[0]) || "unknown error";
      toast.error(`Backfill failed: ${err}`);
    }
    // Refresh the coverage pill so it reflects the newly-written URLs.
    load();
  };

  const startPolling = (jobId) => {
    clearPolling();
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/booking-com/backfill/status/${jobId}`);
        setJob(data);
        if (data.state === "done" || data.state === "error") {
          finalizeJob(data);
        }
      } catch (e) {
        // 404 → the backend restarted mid-poll and dropped the
        // in-memory job. Stop polling so we don't spam requests and
        // clear the localStorage handle so the next admin visit
        // starts clean.
        if (e.response?.status === 404) {
          clearPolling();
          try { localStorage.removeItem("bc_backfill_job_id"); } catch {}
          setJob(null);
          toast.info("Backfill job no longer tracked (backend restart). Kick it off again if needed.");
        } else {
          console.warn("bc-backfill status poll failed", e);
        }
      }
    };
    // Fire immediately so the UI shows the running state within one
    // frame instead of waiting a full second, then every 1s.
    tick();
    pollRef.current = setInterval(tick, 1000);
  };

  // Refresh-reattach: on mount, if a job_id is in localStorage, poll it.
  useEffect(() => {
    let jobId = null;
    try { jobId = localStorage.getItem("bc_backfill_job_id"); } catch {}
    if (jobId) startPolling(jobId);
    return clearPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jobRunning = job?.state === "queued" || job?.state === "running";

  const runBackfill = async () => {
    setBusy(true);
    try {
      await api.post("/admin/booking-com/backfill");
      toast.info("Backfill started — refreshing coverage…");
      setTimeout(load, 3500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backfill failed");
    } finally { setBusy(false); }
  };

  const runForceRebuild = async () => {
    setForceRebuildOpen(false);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/booking-com/backfill/start?force=true");
      const jobId = data.job_id;
      try { localStorage.setItem("bc_backfill_job_id", jobId); } catch {}
      setJob({
        job_id: jobId, processed: 0, total: 0, percent: 0.0,
        state: "queued", started_at: null, finished_at: null,
        errors: [], summary: null,
      });
      toast.info("Force rebuild started — every Booking.com URL is being rewritten with the current CJ PID.");
      startPolling(jobId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Force rebuild failed");
    } finally { setBusy(false); }
  };

  const setPreference = async (value) => {
    try {
      await api.post("/admin/booking-com/preference", { value });
      // Bug (OTA30): invalidate the client-side preference cache so the
      // next "Book your stay" click in this tab honors the new value
      // immediately (otherwise a 30s TTL delays real-time verification).
      _resetBookingPreferenceCache();
      toast.success(`Preference set to ${value}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const copyPostback = async () => {
    try {
      await navigator.clipboard.writeText(status?.postback_url_template || "");
      toast.success("Postback URL copied");
    } catch { toast.error("Clipboard write blocked"); }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/cj/import-conversions", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Import done · inserted ${data.inserted || 0}, dupes ${data.duplicates || 0}, unmatched ${data.unmatched_sid || 0}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  const reconcileRow = async (row) => {
    const email = (reconcileEmails[row.commission_id] || "").trim();
    if (!email) { toast.error("Enter a user email first"); return; }
    try {
      const { data } = await api.post("/admin/cj/reconcile-unmatched", {
        commission_id: row.commission_id,
        user_email: email,
        sale_amount: row.sale_amount,
        commission: row.commission,
        event_date: row.event_date,
      });
      if (data.status === "already_reconciled") {
        toast.info(`Already reconciled · ${row.commission_id}`);
      } else {
        toast.success(`Attributed ${row.commission_id} → ${email}`);
      }
      // Remove the reconciled row from local state
      setImportResult((prev) => prev
        ? { ...prev, unmatched_sample: (prev.unmatched_sample || []).filter((r) => r.commission_id !== row.commission_id), unmatched_sid: Math.max(0, (prev.unmatched_sid || 1) - 1) }
        : prev);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reconcile failed");
    }
  };

  const cov = status?.coverage || { total: 0, with_url: 0, pct: 0, color: "red" };
  const preference = status?.preference || "prefer_booking_com";

  return (
    <section
      data-testid="admin-booking-com-panel"
      className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-5 space-y-4"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-tb-gold/15 border border-tb-gold/40 flex items-center justify-center">
            <ExternalLink className="w-5 h-5 text-tb-navy" />
          </div>
          <div>
            <h2 className="font-serif-it text-tb-navy text-2xl leading-tight">Booking.com CJ</h2>
            <p className="font-hand text-tb-blue/70 text-sm italic mt-0.5">
              Session-only attribution · CJ PID {status?.cj_pid || "—"} · Account {status?.cj_account_id || "—"} · Advertiser {status?.advertiser_id || "—"}.
            </p>
          </div>
        </div>
      </div>

      {/* Stats + coverage */}
      <div className="flex flex-wrap gap-2 font-hand text-tb-navy text-sm">
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-tb-navy ${
          cov.color === "green" ? "bg-tb-sage/25 border-tb-sage/60"
          : cov.color === "amber" ? "bg-tb-gold/15 border-tb-gold/60"
          : "bg-tb-orange/15 border-tb-orange/60"
        }`}
        data-testid="admin-bc-coverage-pill">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor:
            cov.color === "green" ? "#7fa781" : cov.color === "amber" ? "#d4a54a" : "#d97a5c" }} />
          Coverage: <b className="not-italic">{cov.with_url.toLocaleString()}</b> / <b className="not-italic">{cov.total.toLocaleString()}</b> ({cov.pct}%)
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-blue/15 border border-tb-blue/40">
          Clicks 30d <b className="not-italic">{(status?.clicks_30d || 0).toLocaleString()}</b>
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-sage/25 border border-tb-sage/50">
          Conversions <b className="not-italic">{(status?.conversions_total || 0).toLocaleString()}</b>
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-navy text-tb-gold-bright">
          Commission ${(status?.commission_total || 0).toFixed(2)}
        </span>
        {cov.with_url < cov.total && (
          <button
            data-testid="admin-bc-backfill-btn"
            disabled={busy || jobRunning}
            onClick={runBackfill}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-gold text-tb-navy font-hand text-sm border-2 border-tb-gold/60 shadow-sm disabled:opacity-50 hover:brightness-105 transition"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
            Backfill Booking.com URLs
          </button>
        )}
        <button
          data-testid="admin-bc-force-rebuild-btn"
          disabled={busy || jobRunning}
          onClick={() => setForceRebuildOpen(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-orange/15 text-tb-navy font-hand text-sm border-2 border-tb-orange/60 shadow-sm disabled:opacity-50 hover:brightness-105 transition"
          title="Rewrites every existing Booking.com URL with the current CJ PID. Use after a PID / env change."
        >
          {jobRunning ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          {jobRunning ? "Rebuilding Booking.com URLs…" : "Rebuild Booking.com URLs (force)"}
        </button>
      </div>

      {/* Force-rebuild live progress. Rendered whenever a job is
          tracked (running OR just-finished) so the admin sees the
          final summary without racing the toast. Slim bar under the
          pill; label reads "X / Y (Z%)". */}
      {job && (
        <div
          data-testid="admin-bc-force-rebuild-progress"
          className="rounded-xl border border-tb-cream-3 bg-tb-cream-2/40 px-3 py-2.5 space-y-1.5"
        >
          <div className="flex items-center justify-between gap-3 font-hand text-tb-navy text-sm">
            <span
              data-testid="admin-bc-force-rebuild-progress-label"
              className="tabular-nums"
            >
              {job.state === "queued" && "Queued…"}
              {job.state === "running" && (
                <>
                  <b className="not-italic">{(job.processed || 0).toLocaleString()}</b>
                  {" / "}
                  <b className="not-italic">{(job.total || 0).toLocaleString()}</b>
                  {" ("}
                  <b className="not-italic">{(job.percent || 0).toFixed(1)}%</b>
                  {")"}
                </>
              )}
              {job.state === "done" && job.summary && (
                <>
                  <span className="text-tb-sage font-semibold">Done</span>
                  {" · scanned "}
                  <b className="not-italic">{(job.summary.scanned || 0).toLocaleString()}</b>
                  {" · URLs set "}
                  <b className="not-italic">{(job.summary.url_set || 0).toLocaleString()}</b>
                  {" · no-url "}
                  <b className="not-italic">{(job.summary.no_url || 0).toLocaleString()}</b>
                  {" · "}
                  <b className="not-italic">{job.summary.duration_seconds || 0}s</b>
                </>
              )}
              {job.state === "error" && (
                <span className="text-tb-orange font-semibold">
                  Failed{job.errors?.[0] ? ` — ${job.errors[0]}` : ""}
                </span>
              )}
            </span>
            {(job.state === "done" || job.state === "error") && (
              <button
                type="button"
                data-testid="admin-bc-force-rebuild-progress-dismiss"
                onClick={() => setJob(null)}
                className="text-tb-navy/50 hover:text-tb-navy text-xs underline underline-offset-2"
              >
                dismiss
              </button>
            )}
          </div>
          <div className="h-1.5 rounded-full bg-tb-cream-3 overflow-hidden">
            <div
              className={
                "h-full rounded-full transition-all duration-300 " +
                (job.state === "error"
                  ? "bg-tb-orange"
                  : job.state === "done"
                  ? "bg-tb-sage"
                  : "bg-tb-navy")
              }
              style={{ width: `${Math.min(100, Math.max(0, job.percent || 0))}%` }}
              data-testid="admin-bc-force-rebuild-progress-bar"
            />
          </div>
        </div>
      )}

      <AlertDialog open={forceRebuildOpen} onOpenChange={setForceRebuildOpen}>
        <AlertDialogContent data-testid="admin-bc-force-rebuild-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rebuild all Booking.com URLs?</AlertDialogTitle>
            <AlertDialogDescription>
              This overwrites <b>every</b> existing <code>booking_com_url</code> on
              all {(status?.coverage?.total || 0).toLocaleString()} catalog cards using the
              current CJ PID (<code>{status?.cj_pid || "—"}</code>). Ignores idempotency —
              all rows are rewritten. Use this only after a PID / advertiser change.
              Existing anonymous click records are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-bc-force-rebuild-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-bc-force-rebuild-confirm"
              onClick={runForceRebuild}
              className="bg-tb-orange text-white hover:bg-tb-orange/90"
            >
              Rebuild all URLs
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* (OTA32) Provider preference radios removed 2026-07-11.
          Stay22 dropped — every click routes to Booking.com CJ. See
          /app/memory/INTEGRATIONS_REMOVAL.md. */}
      {false && (
      <div className="rounded-xl border border-tb-cream-3 bg-tb-cream-2/40 px-3 py-2 flex flex-wrap items-center gap-4">
        <span className="font-hand text-tb-navy text-sm">Provider preference:</span>
        {[
          { v: "prefer_booking_com", l: "Prefer Booking.com (default)" },
          { v: "booking_com_cj", l: "Booking.com CJ only" },
          { v: "stay22", l: "Stay22 only" },
        ].map((opt) => (
          <label key={opt.v} className="inline-flex items-center gap-1.5 font-hand text-tb-navy text-sm cursor-pointer">
            <input
              type="radio"
              name="bc-preference"
              value={opt.v}
              data-testid={`admin-bc-pref-${opt.v}`}
              checked={preference === opt.v}
              onChange={() => setPreference(opt.v)}
              className="accent-tb-navy"
            />
            {opt.l}
          </label>
        ))}
      </div>
      )}

      {/* Postback URL — (2026-07-12) collapsed by default; the user
          is not doing S2S postback yet. Available under the disclosure
          for when they set it up. */}
      <details className="rounded-xl border border-tb-cream-3 bg-tb-cream-2/40 p-3">
        <summary
          data-testid="admin-bc-postback-details-summary"
          className="cursor-pointer font-hand text-tb-navy text-sm select-none"
        >
          Advanced: CJ Postback URL
        </summary>
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="font-hand text-tb-navy text-sm">Postback URL (paste into CJ dashboard)</span>
            <button
              type="button"
              data-testid="admin-bc-postback-copy"
              onClick={copyPostback}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs shadow hover:brightness-110 transition"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <input
            readOnly
            data-testid="admin-bc-postback-url"
            value={status?.postback_url_template || ""}
            className="w-full px-2 py-1.5 rounded-md bg-tb-cream border-2 border-tb-cream-3 font-ui text-xs text-tb-navy/85"
          />
          <div className="font-hand text-tb-navy/60 text-xs italic">
            Paste into CJ Account → Publisher → Postback URL (or the older Websites → Server-to-Server path). Requires completed Publisher Classification.
          </div>
        </div>
      </details>

      {/* CSV import */}
      <div className="rounded-xl border border-tb-cream-3 bg-tb-cream-2/40 p-3 space-y-2">
        <div className="font-hand text-tb-navy text-sm flex items-center gap-2">
          <Database className="w-4 h-4" /> Import CJ conversions (CSV)
        </div>
        <input
          type="file"
          accept=".csv,.xlsx,.xls,text/csv"
          data-testid="admin-bc-csv-input"
          disabled={busy}
          onChange={(e) => handleFileUpload(e.target.files?.[0])}
          className="block w-full text-sm font-ui text-tb-navy file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-tb-navy file:text-tb-gold-bright file:font-hand file:cursor-pointer disabled:opacity-50"
        />
        {importResult && (
          <div data-testid="admin-bc-import-result" className="rounded-lg border border-tb-cream-3 bg-tb-cream p-2 font-hand text-tb-navy text-sm space-y-1">
            <div>
              Result — <b>{importResult.inserted}</b> inserted · <b>{importResult.duplicates}</b> duplicates · <b>{importResult.unmatched_sid}</b> unmatched · <b>{importResult.wrong_advertiser}</b> wrong-advertiser · <b>{importResult.errored}</b> errored.
            </div>
            {(importResult.unmatched_sample || []).length > 0 && (
              <button
                type="button"
                data-testid="admin-bc-reconcile-open"
                onClick={() => setReconcileOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-orange/20 border border-tb-orange/50 text-tb-navy font-hand text-xs hover:brightness-105 transition"
              >
                <Wrench className="w-3 h-3" /> Reconcile {importResult.unmatched_sample.length} unmatched
              </button>
            )}
          </div>
        )}
      </div>

      {/* Reconcile drawer */}
      <Sheet open={reconcileOpen} onOpenChange={(v) => { if (!v) setReconcileOpen(false); }}>
        <SheetContent side="right" data-testid="admin-bc-reconcile-drawer" className="w-full sm:max-w-2xl overflow-y-auto bg-tb-cream border-l-2 border-tb-cream-3">
          <SheetHeader className="text-left mb-3">
            <SheetTitle className="font-serif-it text-tb-navy text-2xl">Reconcile unmatched conversions</SheetTitle>
            <SheetDescription className="font-hand text-tb-navy/60 italic">
              Manually attribute a CJ conversion to a user by email.
            </SheetDescription>
          </SheetHeader>
          {(importResult?.unmatched_sample || []).length === 0 ? (
            <div className="rounded-xl bg-tb-cream-2/70 border border-tb-cream-3 p-6 text-center font-hand text-tb-navy/60 italic">
              All rows reconciled or no unmatched rows in this import.
            </div>
          ) : (
            <ul className="divide-y divide-tb-cream-3/60 rounded-xl bg-tb-cream-2/50 border border-tb-cream-3 overflow-hidden">
              {(importResult?.unmatched_sample || []).map((r) => (
                <li key={r.commission_id} className="p-3 space-y-2" data-testid={`admin-bc-reconcile-row-${r.commission_id}`}>
                  <div className="font-hand text-tb-navy text-sm">
                    <b>{r.commission_id}</b> · ${r.sale_amount || 0} sale / ${r.commission || 0} commission · {r.event_date || "—"}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="email"
                      placeholder="user email"
                      data-testid={`admin-bc-reconcile-email-${r.commission_id}`}
                      value={reconcileEmails[r.commission_id] || ""}
                      onChange={(e) => setReconcileEmails((prev) => ({ ...prev, [r.commission_id]: e.target.value }))}
                      className="flex-1 min-w-[180px] px-2 py-1 rounded-md bg-tb-cream border-2 border-tb-cream-3 font-ui text-sm"
                    />
                    <button
                      type="button"
                      data-testid={`admin-bc-reconcile-submit-${r.commission_id}`}
                      onClick={() => reconcileRow(r)}
                      className="px-3 py-1.5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs shadow hover:brightness-110 transition"
                    >
                      Attribute to user
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}

